<template>
  <div id="director-screening-report">
    <!-- Page Control for export of data -->
    <PageControl
      :title="'Director Screening Report'"
      :elements_query="sheet_elements"
    >
      <button
        @click="show_export_modal"
        class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
      >
        <i class="bi bi-download"></i>
        Export Data
      </button>

      <!-- Export Modal -->
      <Teleport to="body">
        <div
          class="modal fade"
          :id="'export-modal-' + report_reference"
          tabindex="-1"
          aria-labelledby="exportModalLabel"
          aria-hidden="true"
        >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content rounded-4 shadow">
            <div class="modal-header">
              <h6 class="modal-title fw-semibold text-muted">
                Export Director Screening Report Data
              </h6>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label fw-bold">Select Data to Export:</label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.directors_summary"
                    id="directorsSummary"
                  />
                  <label class="form-check-label" for="directorsSummary">
                    Directors Summary
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.personal_information"
                    id="personalInformation"
                  />
                  <label class="form-check-label" for="personalInformation">
                    Directors Personal Information
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.aml_results"
                    id="amlResults"
                  />
                  <label class="form-check-label" for="amlResults">
                    AML Screening Results
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.id_verification"
                    id="idVerification"
                  />
                  <label class="form-check-label" for="idVerification">
                    RSA ID Verification Results
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.watchlist_data"
                    id="watchlistData"
                  />
                  <label class="form-check-label" for="watchlistData">
                    Watchlist Verification Data
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.raw_json"
                    id="rawJson"
                  />
                  <label class="form-check-label" for="rawJson">
                    Raw JSON Response Data
                  </label>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-bold">Export Format:</label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="csv"
                    id="csvFormat"
                  />
                  <label class="form-check-label" for="csvFormat">
                    CSV (Separate files for each table)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="excel"
                    id="excelFormat"
                  />
                  <label class="form-check-label" for="excelFormat">
                    Excel (Multiple sheets in one file)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="json"
                    id="jsonFormat"
                  />
                  <label class="form-check-label" for="jsonFormat">
                    JSON (JavaScript Object Notation)
                  </label>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
                @click="close_modal"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-bg-green text-xs rounded-small shadow-sm"
                @click="export_data"
                :disabled="!has_selected_tables"
              >
                <i class="bi bi-download me-1"></i>
                Export Data
              </button>
            </div>
          </div>
        </div>
        </div>
      </Teleport>
    </PageControl>

    <PageComponent>

            <SectionSheet>
        <div
          class="container-fluid py-3 header d-flex align-items-center justify-content-between border-bottom mb-3"
        >
          <div class="d-flex">
            <img
              class="logo"
              width="200"
              height="80"
              src="@/assets/images/new_black_logo2.svg"
              alt="The Good People Data Company Logo"
              style="object-fit: contain;"
            />
          </div>
          <div class="d-flex text-end">
            <div>
              <div class="h4 fw-bold mb-1 text-primary">Director Screening Report</div>
              <div class="text-muted" style="font-size: 11px; line-height: 1.3;">
                28 Fricker Road, Illovo<br />
                Johannesburg, 2196<br />
                South Africa<br />
                <span class="text-primary">www.goodpeopledata.com</span>
              </div>
            </div>
          </div>
        </div>

        <div class="card shadow-sm border bg-white mb-2">
          <div class="card-header">
            <h6 class="mb-0">
              <i class="bi bi-people-fill me-2 text-primary"></i>
              Director Screening Summary
            </h6>
          </div>
          <div class="card-body">
            <div class="alert alert-light border-primary mb-3">
              <i class="bi bi-info-circle text-primary me-2"></i>
              <strong>Comprehensive Verification Report:</strong> This report presents detailed screening results for 
              <span class="fw-bold text-primary">{{ directors_found }}</span> director(s) identified, with 
              <span class="fw-bold text-success">{{ directors_screened }}</span> successfully processed through our verification systems.
            </div>
            
            <p class="mb-3" style="font-size: 12px; line-height: 1.5;">
              Our multi-layered screening process encompasses <strong>Anti-Money Laundering (AML)</strong> database checks, 
              <strong>international watchlist verification</strong>, and <strong>South African Home Affairs ID validation</strong> 
              through both the National Identification System (HANIS) and National Population Register (NPR). 
              Each director undergoes thorough identity validation and comprehensive risk assessment to ensure 
              full regulatory compliance.
            </p>
            
            <!-- Enhanced Status Cards -->
            <div class="row g-3 justify-content-center">
              <div class="col-md-3">
                <div class="status-card-enhanced">
                  <div class="status-icon-enhanced" :class="aml_status_class">
                    <i class="bi bi-shield-check"></i>
                  </div>
                  <div class="status-content-enhanced">
                    <div class="status-label-enhanced">AML Status</div>
                    <div class="status-value-enhanced">{{ aml_status }}</div>
                    <div class="status-detail-enhanced">Overall screening result</div>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="status-card-enhanced">
                  <div class="status-icon-enhanced bg-primary">
                    <i class="bi bi-list-check"></i>
                  </div>
                  <div class="status-content-enhanced">
                    <div class="status-label-enhanced">Match Count</div>
                    <span></span><div class="status-value-enhanced">{{ match_count }}</div>
                    <div class="status-detail-enhanced">Watchlist matches found</div>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="status-card-enhanced">
                  <div class="status-icon-enhanced" :class="decision_class">
                    <i class="bi bi-check-circle"></i>
                  </div>
                  <div class="status-content-enhanced">
                    <div class="status-label-enhanced">Decision</div>
                    <div class="status-value-enhanced">{{ screening_decision }}</div>
                    <div class="status-detail-enhanced">Watchlist decision</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionSheet>

      <!-- Loop through each director - 3 pages per director -->
      <template v-for="(director, index) in directors" :key="index">
        
        <!-- PAGE 1: Director Overview & Personal Information -->
        <SectionSheet>
          <!-- Director Header with Download Button -->
          <div class="director-header">
            <h5>Director {{ index + 1 }}: {{ director.director.full_name }}</h5>

          </div>

          <!-- Director Personal Information -->
          <div class="card">
            <div class="card-header">
              <h6>Director Personal Information</h6>
              <span
                class="status-badge"
                :class="getStatusBadgeClass(director.director.status)"
              >
                {{ director.director.status }}
              </span>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <!-- Full Name -->
                <div class="col-md-6">
                  <div class="status-card-simple">
                    <div class="status-icon-simple">
                      <i class="bi bi-person"></i>
                    </div>
                    <div class="status-content-simple">
                      <div class="status-label-simple">Full Name</div>
                      <div class="status-value-simple bg-white">
                        {{ director.director.full_name }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ID Number -->
                <div class="col-md-6">
                  <div class="status-card-simple">
                    <div class="status-icon-simple">
                      <i class="bi bi-hash"></i>
                    </div>
                    <div class="status-content-simple">
                      <div class="status-label-simple">ID Number</div>
                      <div class="status-value-simple bg-white">
                        {{ director.director.id_number }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Director Status -->
                <div class="col-md-6">
                  <div class="status-card-simple">
                    <div class="status-icon-simple">
                      <i class="bi bi-person-badge"></i>
                    </div>
                    <div class="status-content-simple">
                      <div class="status-label-simple">Director Status</div>
                      <div
                        class="status-value-simple bg-white"
                        :class="getStatusTextClass(director.director.status)"
                      >
                        {{ director.director.status }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Appointment Date -->
                <div class="col-md-6" v-if="director.director.appointment_date">
                  <div class="status-card-simple">
                    <div class="status-icon-simple">
                      <i class="bi bi-calendar-event"></i>
                    </div>
                    <div class="status-content-simple">
                      <div class="status-label-simple">Appointment Date</div>
                      <div class="status-value-simple bg-white">
                        {{ formatDate(director.director.appointment_date) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Overview Status Cards -->
          <div class="card">
            <div class="card-header">
              <h6>Verification Overview</h6>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-center align-items-center">
                <div class="row g-4 justify-content-center" style="max-width: 800px;">
                  <div class="col-lg-4 col-md-6 d-flex justify-content-center">
                    <div class="status-card-enhanced">
                      <div class="status-icon-enhanced bg-success">
                        <i class="bi bi-list-check"></i>
                      </div>
                      <div class="status-content-enhanced">
                        <div class="status-label-enhanced">AML Screening</div>
                        <div class="status-value-enhanced">
                          {{ getCleanAMLStatus(director.aml_screening?.Status) }}
                        </div>
                        <div class="status-detail-enhanced">Watchlist Status</div>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-4 col-md-6 d-flex justify-content-center">
                    <div class="status-card-enhanced">
                      <div class="status-icon-enhanced bg-info">
                        <i class="bi bi-person-check"></i>
                      </div>
                      <div class="status-content-enhanced">
                        <div class="status-label-enhanced">Name Match</div>
                        <div class="status-value-enhanced">
                          {{ getAverageNameMatch(director) }}%
                        </div>
                        <div class="status-detail-enhanced">Identity Confidence</div>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-4 col-md-6 d-flex justify-content-center">
                    <div class="status-card-enhanced">
                      <div class="status-icon-enhanced" :class="getRiskLevelClass(director)">
                        <i class="bi bi-exclamation-triangle"></i>
                      </div>
                      <div class="status-content-enhanced">
                        <div class="status-label-enhanced">Risk Level</div>
                        <div class="status-value-enhanced">
                          {{ calculateRiskLevel(director) }}
                        </div>
                        <div class="status-detail-enhanced">Overall Assessment</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionSheet>

        <!-- PAGE 2: RSA ID Verification & Identity Details -->
        <SectionSheet>
          <!-- Page Header -->
          <div class="director-header">
            <h5>Director {{ index + 1 }}: {{ director.director.full_name }}</h5>

          </div>

          <!-- RSA ID Verification Results -->
          <div class="card" v-if="director.rsa_id_verification && director.rsa_id_verification.response">
            <div class="card-header">
              <h6>RSA ID Verification Results</h6>
              <span
                class="status-badge"
                :class="getIDVerificationBadgeClass(getCleanVerificationStatus(director.rsa_id_verification.response.Status, director))"
              >
                {{ getCleanVerificationStatus(director.rsa_id_verification.response.Status, director) }}
              </span>
            </div>
            <div class="card-body">
              <!-- Verification Status Message -->
              <div class="alert alert-info text-xs" v-if="director.rsa_id_verification.response.Message">
                <i class="bi bi-info-circle me-2"></i>
                <strong>Verification Message:</strong> {{ director.rsa_id_verification.response.Message }}
              </div>

              <!-- Official Photo Section -->
              <div class="row g-3 mb-4" v-if="director.rsa_id_verification.response.FacialImageAvailable">
                <div class="col-md-12">
                  <div class="photo-section">
                    <h6 class="mb-3 text-center">
                      <i class="bi bi-camera me-2"></i>Official Identity Photograph
                    </h6>
                    <div class="photo-container">
                      <div class="photo-frame">
                        <img
                          v-if="hasValidBase64Image(director.rsa_id_verification.response.FacialImage)"
                          :src="getBase64ImageSrc(director.rsa_id_verification.response.FacialImage)"
                          alt="Official ID Photo"
                          class="official-photo"
                          @error="handleImageError"
                          @load="onImageLoad"
                        />
                        <div v-else class="photo-placeholder">
                          <i class="bi bi-person-square"></i>
                          <p>Official Photo</p>
                          <small>Not Available</small>
                        </div>
                      </div>
                      <div class="photo-caption">
                        <small class="text-muted">
                          <i class="bi bi-shield-check me-1"></i>
                          Verified through SA Home Affairs Database
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Personal Information Cards -->
              <div class="row g-3">
                <!-- First Name with Match Result -->
                <div class="col-md-6">
                  <div class="info-card-enhanced">
                    <div class="info-icon-enhanced bg-primary">
                      <i class="bi bi-person"></i>
                    </div>
                    <div class="info-content-enhanced">
                      <div class="info-label-enhanced">First Name</div>
                      <div class="info-value-enhanced">
                        {{ director.rsa_id_verification.response.FirstName || 'N/A' }}
                      </div>
                      <div v-if="director.rsa_id_verification.response.FirstNameResult" class="info-match-enhanced">
                        <span class="match-badge" :class="getMatchResultBadge(director.rsa_id_verification.response.FirstNameResult)">
                          {{ director.rsa_id_verification.response.FirstNameResult }}% Match
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Last Name with Match Result -->
                <div class="col-md-6">
                  <div class="info-card-enhanced">
                    <div class="info-icon-enhanced bg-success">
                      <i class="bi bi-person-vcard"></i>
                    </div>
                    <div class="info-content-enhanced">
                      <div class="info-label-enhanced">Last Name</div>
                      <div class="info-value-enhanced">
                        {{ director.rsa_id_verification.response.LastName || 'N/A' }}
                      </div>
                      <div v-if="director.rsa_id_verification.response.LastNameResult" class="info-match-enhanced">
                        <span class="match-badge" :class="getMatchResultBadge(director.rsa_id_verification.response.LastNameResult)">
                          {{ director.rsa_id_verification.response.LastNameResult }}% Match
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- ID Number -->
                <div class="col-md-6">
                  <div class="info-card-enhanced">
                    <div class="info-icon-enhanced bg-info">
                      <i class="bi bi-hash"></i>
                    </div>
                    <div class="info-content-enhanced">
                      <div class="info-label-enhanced">ID Number</div>
                      <div class="info-value-enhanced">
                        {{ director.rsa_id_verification.response.IdNumber || 'N/A' }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Birth Place -->
                <div class="col-md-6">
                  <div class="info-card-enhanced">
                    <div class="info-icon-enhanced bg-warning">
                      <i class="bi bi-geo-alt"></i>
                    </div>
                    <div class="info-content-enhanced">
                      <div class="info-label-enhanced">Birth Place</div>
                      <div class="info-value-enhanced">
                        {{ director.rsa_id_verification.response.BirthPlaceCountry || 'N/A' }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Marital Status -->
                <div class="col-md-6">
                  <div class="info-card-enhanced">
                    <div class="info-icon-enhanced bg-danger">
                      <i class="bi bi-heart"></i>
                    </div>
                    <div class="info-content-enhanced">
                      <div class="info-label-enhanced">Marital Status</div>
                      <div class="info-value-enhanced">
                        {{ director.rsa_id_verification.response.MaritalStatus || 'N/A' }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Smart Card Status -->
                <div class="col-md-6">
                  <div class="info-card-enhanced">
                    <div class="info-icon-enhanced bg-secondary">
                      <i class="bi bi-credit-card"></i>
                    </div>
                    <div class="info-content-enhanced">
                      <div class="info-label-enhanced">Smart Card Status</div>
                      <div class="info-value-enhanced">
                        {{ director.rsa_id_verification.response.SmartCardIssued ? 'Issued' : 'Not Issued' }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Tracking Number -->
                <div class="col-md-12" v-if="director.rsa_id_verification.response.TrackingNumber">
                  <div class="info-card-enhanced">
                    <div class="info-icon-enhanced bg-dark">
                      <i class="bi bi-fingerprint"></i>
                    </div>
                    <div class="info-content-enhanced">
                      <div class="info-label-enhanced">Home Affairs Tracking Number</div>
                      <div class="info-value-enhanced" style="font-size: 0.9rem; font-family: monospace;">
                        {{ director.rsa_id_verification.response.TrackingNumber }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionSheet>

        <!-- PAGE 3: AML Screening Results -->
        <SectionSheet>
          <!-- Page Header -->
          <div class="container-fluid py-3 header d-flex align-items-center justify-content-between border-bottom mb-3">
            <div class="d-flex">
              <img
                class="logo"
                width="295"
                height="125"
                src="@/assets/images/new_black_logo2.svg"
                alt="Logo"
              />
            </div>
            <div class="d-flex text-end">
              <div>
                <div class="h4 fw-bold mb-1">Anti-Money Laundering Report</div>
                <div class="text-muted text-xs">
                  Director: {{ director.director.full_name }}
                </div>
              </div>
            </div>
          </div>

          <!-- AML Screening Summary -->
          <div class="card shadow-sm border bg-white mb-2" v-if="getTotalAMLMatches(director) > 0">
            <div class="card-header">
              <h6 class="mb-0">AML Screening Summary</h6>
            </div>
            <div class="card-body">
              <p>
                This report presents Anti-Money Laundering screening results for
                <span class="fw-bold text-dark">
                  {{ director.director.full_name }}
                </span>
                (Date of Birth: {{ getDirectorDateOfBirth(director) }}, Country: {{ getDirectorCountry(director) }}). 
                The comprehensive screening was conducted against watchlist databases.
              </p>
              <div class="d-flex justify-content-center align-items-center m-0 p-2 bg-white">
                <div class="provider-box text-center">
                  <span class="fw-bold">
                    {{ getTotalAMLMatches(director) }} potential match(es) found
                  </span>
                  <br />
                  Decision: <strong>{{ getWatchlistDecision(director) }}</strong>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="director.aml_screening">
            <div class="card bg-white mb-4 shadow-sm border p-2">
              <div class="card-header">
                <h6 class="mb-0">AML Screening Summary</h6>
              </div>
              <div class="card-body">
                <p class="mb-3">
                  This <strong>Anti-Money Laundering Verification</strong> was
                  performed for
                  <strong>{{ director.director.full_name }}</strong>
                  <span v-if="getDirectorDateOfBirth(director)">
                    (Date of Birth: {{ getDirectorDateOfBirth(director) }})</span>
                  <span v-if="getDirectorCountry(director)"> from {{ getDirectorCountry(director) }}</span>. 
                  The subject was thoroughly screened against multiple databases to ensure compliance.
                </p>

                <div class="text-center py-3">
                  <div class="alert alert-success mb-0">
                    <i class="fas fa-check-circle fa-3x text-success mb-3"></i>
                    <h5 class="text-success">No Matches Found</h5>
                    <p class="mb-0">
                      The director has been cleared through our comprehensive AML screening process.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Results Summary Card - Updated for available data -->
          <div class="row mb-2" v-if="getTotalAMLMatches(director) > 0">
            <div class="col-12">
              <div class="card shadow-sm border mb-3">
                <div class="card-body p-0">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th class="ps-3">Category</th>
                          <th class="text-center">Results</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td class="ps-3">
                            <div class="d-flex align-items-center">
                              <i class="fas fa-newspaper text-warning me-2"></i>
                              <span class="fw-bold">Negative Media</span>
                            </div>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-light text-dark border">{{
                              get_nm_count(director)
                            }}</span>
                          </td>
                          <td class="text-muted small">
                            Matches found in negative media databases indicating
                            potential adverse publicity.
                          </td>
                        </tr>
                        <tr>
                          <td class="ps-3">
                            <div class="d-flex align-items-center">
                              <i class="fas fa-user-tie text-info me-2"></i>
                              <span class="fw-bold"
                                >Politically Exposed Person</span
                              >
                            </div>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-light text-dark border">{{
                              get_pep_count(director)
                            }}</span>
                          </td>
                          <td class="text-muted small">
                            Matches found in PEP databases for individuals in
                            prominent political positions.
                          </td>
                        </tr>
                        <tr>
                          <td class="ps-3">
                            <div class="d-flex align-items-center">
                              <i
                                class="fas fa-exclamation-triangle text-danger me-2"
                              ></i>
                              <span class="fw-bold">Watchlist</span>
                            </div>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-light text-dark border">{{
                              get_wls_count(director)
                            }}</span>
                          </td>
                          <td class="text-muted small">
                            Matches found in global watchlist databases for
                            sanctions or regulatory concerns.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Subject Information -->
          <div class="card shadow-sm border bg-white p-2 mb-2">
            <div class="card-header">
              <h6 class="mb-0">Subject Information</h6>
            </div>
            <div class="card-body">
              <table class="info-table table table-sm">
                <tr>
                  <td>Full Name</td>
                  <td>{{ director.director.full_name || "N/A" }}</td>
                </tr>
                <tr>
                  <td>First Name</td>
                  <td>{{ getDirectorFirstName(director) || "N/A" }}</td>
                </tr>
                <tr>
                  <td>Last Name</td>
                  <td>{{ getDirectorLastName(director) || "N/A" }}</td>
                </tr>
                <tr>
                  <td>Date of Birth</td>
                  <td>{{ getDirectorDateOfBirth(director) || "N/A" }}</td>
                </tr>
                <tr>
                  <td>Gender</td>
                  <td>{{ getDirectorGender(director) || "N/A" }}</td>
                </tr>
                <tr>
                  <td>Country</td>
                  <td>{{ getDirectorCountry(director) || "N/A" }}</td>
                </tr>
                <tr>
                  <td>Director ID</td>
                  <td>{{ director.director.id_number || "N/A" }}</td>
                </tr>
              </table>
            </div>
          </div>
        </SectionSheet>

        <template v-if="director.aml_results && director.aml_results.nm_results && director.aml_results.nm_results.length > 0">
          <template
            v-for="(chunk, chunk_index) in Math.ceil(get_limited_nm_results(director).length / 2)"
            :key="`nm-result-${index}-${chunk_index}`"
          >
            <SectionSheet>
              <template v-if="chunk_index === 0">
                <div class="card shadow-sm border bg-white p-2 mb-3 nm-card">
                  <div class="card-header">
                    <h6 class="mb-0">Negative Media Results - {{ get_director_full_name(director) }}</h6>
                  </div>
                  <div class="card-body">
                    <p class="text-muted mb-0">
                      The following results were found in negative media screening databases.
                    </p>
                  </div>
                </div>
              </template>
              
              <template
                v-for="(result, i) in get_limited_nm_results(director).slice(
                  chunk_index === 0 ? 0 : chunk_index * 2 - 1,
                  chunk_index === 0 ? 1 : chunk_index * 2 - 1 + 2
                )"
                :key="`nm-result-${index}-${chunk_index === 0 ? i : chunk_index * 2 - 1 + i}`"
              >
                <div class="card border border-left-accent nm-card mb-3">
                  <div class="card-body p-3">
                    <div class="nm-result-card">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div class="result-header">
                          <span class="badge text-white" style="background-color: #036016">Score: {{ result.score }}</span>
                          <span class="badge bg-secondary ms-2">Category: {{ result.category }}</span>
                          <span v-if="result.concepts && result.concepts.length" class="badge bg-light text-dark ms-2">
                            Concepts: {{ result.concepts.join(", ") }}
                          </span>
                        </div>
                        <small class="text-muted">ID: {{ result.productId }}</small>
                      </div>
                      
                      <div class="rounded p-2 bg-light">
                        <div v-if="result.articleTitle" class="mb-2 pt-2">
                          <div class="d-flex align-items-center">
                            <i class="bi bi-newspaper me-2"></i>
                            <span class="fw-bold text-dark h6 mb-0" :title="result.articleTitle">
                              {{ result.articleTitle }}
                            </span>
                          </div>
                          <div v-if="result.articleDate" class="text-muted small ms-4">
                            Published: {{ new Date(result.articleDate).toLocaleDateString() }}
                          </div>
                        </div>

                        <div class="row mb-2">
                          <div class="col-md-4 mb-2 text-center">
                            <div class="text-muted small">Subject</div>
                            <div class="fw-bold text-dark">{{ result.subjectMatched }}</div>
                          </div>
                          <div class="col-md-4 mb-2 text-center">
                            <div class="text-muted small">Country</div>
                            <div class="fw-bold text-dark">{{ result.sourceCountry }}</div>
                          </div>
                          <div class="col-md-4 mb-2 text-center">
                            <div class="text-muted small">Source ID</div>
                            <div class="fw-bold text-dark">{{ result.sourceId }}</div>
                          </div>
                          <div class="col-md-4 mb-2 text-center">
                            <div class="text-muted small">Subject Age</div>
                            <div class="fw-bold text-dark">{{ result.subjectAge || "Unavailable" }}</div>
                          </div>
                          <div class="col-md-4 mb-2 text-center">
                            <div class="text-muted small">Location</div>
                            <div class="fw-bold text-dark">{{ result.subjectLocation || "Unavailable" }}</div>
                          </div>
                          <div class="col-md-4 mb-2 text-center">
                            <div class="text-muted small">Sentiment</div>
                            <div class="fw-bold text-dark">{{ result.sentimentConfidenceLevel || "Unavailable" }}</div>
                          </div>
                        </div>
                      </div>
                      
                      <div v-if="(result.highlights && result.highlights.length) || result.url" class="pt-2">
                        <div v-if="result.highlights && result.highlights.length" class="mb-2">
                          <strong>Key Highlights:</strong>
                          <template v-if="result.highlights.length <= 3">
                            <div class="mb-0 mt-1 p-2 rounded border">
                              <ul>
                                <li v-for="(highlight, hi) in result.highlights" :key="hi" class="small">
                                  <span v-html="highlight.length > 500 ? highlight.substring(0, 500) + '...' : highlight"></span>
                                </li>
                              </ul>
                            </div>
                          </template>
                          <template v-else>
                            <div class="mb-0 mt-1 p-2 rounded border">
                              <ul>
                                <li v-for="(highlight, hi) in result.highlights.slice(0, 2)" :key="hi" class="small">
                                  <span v-html="highlight.length > 500 ? highlight.substring(0, 500) + '...' : highlight"></span>
                                </li>
                              </ul>
                              <span class="text-muted small d-block mt-1">+{{ result.highlights.length - 2 }} more highlights</span>
                            </div>
                          </template>
                        </div>
                        <div v-if="result.url" class="mt-2">
                          <strong>Source URL:</strong>
                          <a :href="result.url" target="_blank" class="text-decoration-none small d-block mt-1">
                            {{ result.url }}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

            </SectionSheet>
          </template>
        </template>

        <template v-if="nm_exceeds_limit(director) && director.aml_results.nm_results && director.aml_results.nm_results.length > 0">
          <SectionSheet>
            <div class="limit-exceeded-notice mb-4">
              <div class="limit-card nm-limit">
                <div class="limit-header">
                  <div class="limit-icon">
                    <i class="fas fa-exclamation-triangle"></i>
                  </div>
                  <div class="limit-title">
                    <h6>Results Limit Reached</h6>
                    <span class="limit-subtitle">Negative Media Results - {{ get_director_full_name(director) }}</span>
                  </div>
                </div>
                <div class="limit-content">
                  <div class="limit-stats">
                    <div class="stat-item">
                      <span class="stat-label">Showing</span>
                      <span class="stat-value">{{ get_limited_nm_results(director).length }}</span>
                    </div>
                    <div class="stat-divider">of</div>
                    <div class="stat-item">
                      <span class="stat-label">Total</span>
                      <span class="stat-value">{{ get_nm_count(director) }}</span>
                    </div>
                  </div>
                  <div class="limit-message">
                    <i class="fas fa-info-circle"></i>
                    <span>{{ get_nm_count(director) - get_limited_nm_results(director).length }} additional results available via export</span>
                  </div>
                  <div class="limit-action">
                    <i class="fas fa-download"></i>
                    <span>Use the Export feature above to download complete data in CSV or Excel format</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="report-meta">
              <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
              <div><span class="text-muted">Director:</span> {{ index + 1 }} of {{ directors.length }}</div>
              <div><span class="text-muted">Generated:</span> {{ generated_date }}</div>
            </div>
          </SectionSheet>
        </template>

        <template v-if="!director.aml_results || !director.aml_results.nm_results || director.aml_results.nm_results.length === 0">
          <SectionSheet>
            <div class="card shadow-sm border bg-white mb-4">
              <div class="card-header">
                <h6 class="mb-0">Negative Media Results - {{ get_director_full_name(director) }}</h6>
              </div>
              <div class="card-body text-center py-4">
                <div class="alert alert-info mb-0">
                  <i class="fas fa-info-circle fa-2x text-info mb-3"></i>
                  <h6 class="text-info">No Negative Media Records Found</h6>
                  <p class="mb-0">
                    No adverse media coverage or negative publicity was found for
                    <strong>{{ get_director_full_name(director) }}</strong> in
                    our comprehensive negative media databases.
                  </p>
                </div>
              </div>
            </div>
          </SectionSheet>
        </template>

        <template v-if="director.aml_results && director.aml_results.pep_results && director.aml_results.pep_results.length > 0">
          <template
            v-for="(page, pageIndex) in get_pep_result_pages(director)"
            :key="`pep-page-${index}-${pageIndex}`"
          >
            <SectionSheet>
              <template v-if="page.isFirst">
                <div class="card shadow-sm border p-2 bg-white mb-3 pep-card">
                  <div class="card-header">
                    <h6 class="mb-0">Politically Exposed Person (PEP) Results - {{ get_director_full_name(director) }}</h6>
                  </div>
                  <div class="card-body">
                    <p class="text-muted mb-0">
                      The following results were found in PEP screening databases.
                    </p>
                  </div>
                </div>
              </template>

              <template
                v-for="(result, resultIndex) in page.results"
                :key="`pep-result-${index}-${pageIndex}-${resultIndex}`"
              >
                <div class="card border border-left-accent pep-card mb-3">
                  <div class="card-body p-3">
                    <div class="pep-result-card">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div class="result-header">
                          <span class="badge text-white" style="background-color: #358826">Score: {{ result.score }}</span>
                          <span v-if="result.gender" class="badge bg-dark text-white ms-2">Gender: {{ result.gender }}</span>
                          <span v-if="result.pepSearchType !== undefined" class="badge bg-secondary ms-2">
                            Type: {{ result.pepSearchType }}
                          </span>
                        </div>
                        <small class="text-muted">Profile ID: {{ result.profileId }}</small>
                      </div>
                      
                      <div class="row mb-2">
                        <div class="col-md-12 rounded p-2 bg-light">
                          <div class="row g-3">
                            <div class="col-md-4 mb-2 text-start">
                              <div class="text-muted small">Name</div>
                              <div class="fw-bold text-dark">{{ result.name }}</div>
                            </div>
                            <div class="col-md-4 mb-2 text-center">
                              <div class="text-muted small">Birth Name</div>
                              <div class="fw-bold text-dark">{{ result.birthName }}</div>
                            </div>
                            <div class="col-md-4 mb-2 text-end">
                              <div class="text-muted small">Country</div>
                              <div class="fw-bold text-dark">{{ result.country }}</div>
                            </div>
                            <div class="col-md-4 mb-2 text-start">
                              <div class="text-muted small">Date of Birth</div>
                              <div class="fw-bold text-dark">
                                {{ new Date(result.dateOfBirth).toLocaleDateString() }}
                              </div>
                            </div>
                            <div class="col-md-4 mb-2 text-center">
                              <div class="text-muted small">Date of Death</div>
                              <div class="fw-bold text-dark">
                                {{ new Date(result.dateOfDeath).toLocaleDateString() }}
                              </div>
                            </div>
                            <div class="col-md-4 mb-2 text-end">
                              <div class="text-muted small">Place of Birth</div>
                              <div class="fw-bold text-dark">{{ result.placeOfBirth }}</div>
                            </div>
                          </div>
                        </div>
                        
                        <div class="col-md-12">
                          <div v-if="result.professionalHistory?.length">
                            <strong>Professional History:</strong><br />
                            <div class="mt-1">
                              <span
                                v-for="(position, index) in result.professionalHistory"
                                :key="index"
                                class="badge bg-secondary small text-white me-1 mb-1"
                              >
                                {{ position }}
                              </span>
                            </div>
                          </div>

                          <div v-if="result.aliases?.length" class="mt-1">
                            <strong>Known Aliases:</strong><br />
                            <div class="mt-1">
                              <span
                                v-for="(alias, index) in result.aliases"
                                :key="index"
                                class="badge bg-dark small text-white me-1 mb-1"
                              >
                                {{ alias }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="result.familyMembers?.length" class="mt-2 rounded">
                        <strong>Family Members:</strong>
                        <div class="mt-2 d-flex flex-wrap">
                          <div
                            v-for="(member, mi) in result.familyMembers"
                            :key="mi"
                            class="me-2 mb-2 border rounded px-2 py-1 bg-white"
                          >
                            <div class="d-flex align-items-center">
                              <i class="bi bi-person-fill me-2 text-secondary"></i>
                              <span class="badge bg-secondary">{{ member.name }}</span>
                              <span class="ms-1 text-muted small">({{ member.relationType }})</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="result.scoreBoosts?.length" class="mt-2">
                        <strong>Score Details:</strong>
                        <p class="text-muted small mb-0 fst-italic">
                          {{ result.scoreBoosts[0] }}
                        </p>
                      </div>
                      
                      <div v-if="result.sourceInfoURL?.length" class="mt-3 pt-2 border-top">
                        <strong>Sources:</strong>
                        <div class="bg-light p-2 rounded mt-1">
                          <div
                            v-for="(url, ui) in result.sourceInfoURL.slice(0, 2)"
                            :key="ui"
                            class="small text-truncate"
                          >
                            <a :href="url" target="_blank" class="text-decoration-none">
                              {{ url }}
                            </a>
                          </div>
                          <div
                            v-if="result.sourceInfoURL.length > 2"
                            class="text-muted small fst-italic"
                          >
                            +{{ result.sourceInfoURL.length - 2 }} more sources
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

            </SectionSheet>
          </template>
        </template>

        <template v-if="pep_exceeds_limit(director) && director.aml_results.pep_results && director.aml_results.pep_results.length > 0">
          <SectionSheet>
            <div class="limit-exceeded-notice mb-4">
              <div class="limit-card pep-limit">
                <div class="limit-header">
                  <div class="limit-icon">
                    <i class="fas fa-exclamation-triangle"></i>
                  </div>
                  <div class="limit-title">
                    <h6>Results Limit Reached</h6>
                    <span class="limit-subtitle">PEP Results - {{ get_director_full_name(director) }}</span>
                  </div>
                </div>
                <div class="limit-content">
                  <div class="limit-stats">
                    <div class="stat-item">
                      <span class="stat-label">Showing</span>
                      <span class="stat-value">{{ get_limited_pep_results(director).length }}</span>
                    </div>
                    <div class="stat-divider">of</div>
                    <div class="stat-item">
                      <span class="stat-label">Total</span>
                      <span class="stat-value">{{ get_pep_count(director) }}</span>
                    </div>
                  </div>
                  <div class="limit-message">
                    <i class="fas fa-info-circle"></i>
                    <span>{{ get_pep_count(director) - get_limited_pep_results(director).length }} additional results available via export</span>
                  </div>
                  <div class="limit-action">
                    <i class="fas fa-download"></i>
                    <span>Use the Export feature above to download complete data in CSV or Excel format</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="report-meta">
              <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
              <div><span class="text-muted">Director:</span> {{ index + 1 }} of {{ directors.length }}</div>
              <div><span class="text-muted">Generated:</span> {{ generated_date }}</div>
            </div>
          </SectionSheet>
        </template>

        <template v-if="!director.aml_results || !director.aml_results.pep_results || director.aml_results.pep_results.length === 0">
          <SectionSheet>
            <div class="card shadow-sm border bg-white mb-4">
              <div class="card-header">
                <h6 class="mb-0">Politically Exposed Person (PEP) Results - {{ get_director_full_name(director) }}</h6>
              </div>
              <div class="card-body text-center py-4">
                <div class="alert alert-info mb-0">
                  <i class="fas fa-user-tie fa-2x text-info mb-3"></i>
                  <h6 class="text-info">No PEP Records Found</h6>
                  <p class="mb-0">
                    <strong>{{ get_director_full_name(director) }}</strong> was
                    not found in any Politically Exposed Person databases. No
                    matches were identified for individuals holding prominent
                    political positions or their associates.
                  </p>
                </div>
              </div>
            </div>
          </SectionSheet>
        </template>

        <template v-if="director.aml_results && director.aml_results.wls_results && director.aml_results.wls_results.length > 0">
          <template
            v-for="(chunk, chunk_index) in Math.ceil(get_limited_wls_results(director).length / 2)"
            :key="`wls-result-${index}-${chunk_index}`"
          >
            <SectionSheet>

              <template v-if="chunk_index === 0">
                <div class="card shadow-sm border bg-white p-2 mb-3 wls-card">
                  <div class="card-header">
                    <h6 class="mb-0">Watchlist Results - {{ get_director_full_name(director) }}</h6>
                  </div>
                  <div class="card-body">
                    <p class="text-muted mb-0">
                      The following results were found in sanctions and watchlist databases.
                    </p>
                  </div>
                </div>
              </template>
              
              <template
                v-for="(result, i) in get_limited_wls_results(director).slice(
                  chunk_index === 0 ? 0 : chunk_index * 2 - 1,
                  chunk_index === 0 ? 1 : chunk_index * 2 - 1 + 2
                )"
                :key="`wls-result-${index}-${chunk_index === 0 ? i : chunk_index * 2 - 1 + i}`"
              >
                <div class="card border border-left-accent wls-card mb-3">
                  <div class="card-body p-3">
                    <div class="wls-result-card">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div class="result-header">
                          <span class="badge text-white" style="background-color: #007313">Score: {{ result.score }}</span>
                          <span class="badge bg-dark ms-2">Category: {{ result.category }}</span>
                        </div>
                        <small class="text-muted">ID: {{ result.productId }}</small>
                      </div>
                      
                      <div class="row mb-2 p-2">
                        <div class="col-md-12 rounded p-2 bg-light">
                          <div class="row">
                            <div class="col-md-4 mb-1 text-start">
                              <div class="text-muted small">Subject</div>
                              <div class="fw-bold text-dark">{{ result.subjectMatched }}</div>
                            </div>
                            <div class="col-md-4 mb-1 text-center">
                              <div class="text-muted small">Country</div>
                              <div class="fw-bold text-dark">{{ result.sourceCountry }}</div>
                            </div>
                            <div class="col-md-4 mb-1 text-end">
                              <div class="text-muted small">Source ID</div>
                              <div class="fw-bold text-dark">{{ result.sourceId }}</div>
                            </div>
                            <div class="col-md-4 mb-1 text-start">
                              <div class="text-muted small">Source Agency</div>
                              <div class="fw-bold text-dark">{{ result.sourceAgencyName }}</div>
                            </div>
                            <div class="col-md-4 mb-1 text-center">
                              <div class="text-muted small">Region</div>
                              <div class="fw-bold text-dark">{{ result.sourceRegion }}</div>
                            </div>
                            <div class="col-md-4 mb-1 text-end">
                              <div class="text-muted small">Agency</div>
                              <div class="fw-bold text-dark">{{ result.sourceAgencyAcronym }}</div>
                            </div>
                            <div class="col-md-4 mb-1 text-start">
                              <div class="text-muted small">List Type</div>
                              <div class="fw-bold text-dark">{{ result.sourceListType }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="result.text" class="mt-2 mb-2">
                        <strong>Content:</strong>
                        <p class="text-muted small bg-light p-2 rounded mt-1">
                          {{ result.text.length > 250 ? result.text.substring(0, 250) + "..." : result.text }}
                        </p>
                      </div>

                      <div v-if="result.remarks" class="mt-2">
                        <strong>Remarks:</strong>
                        <p class="text-muted small mb-0 fst-italic">{{ result.remarks }}</p>
                      </div>
                      
                      <div v-if="(result.highlights && result.highlights.length) || result.url" class="mt-3 pt-2 border-top">
                        <div v-if="result.highlights && result.highlights.length">
                          <strong>Key Highlights:</strong>
                          <template v-if="result.highlights.length <= 3">
                            <div class="mb-0 p-2 rounded border">
                              <ul>
                                <li v-for="(highlight, hi) in result.highlights" :key="hi" class="small">
                                  <span v-html="highlight.length > 500 ? highlight.substring(0, 500) + '...' : highlight"></span>
                                </li>
                              </ul>
                            </div>
                          </template>
                          <template v-else>
                            <div class="mb-0 p-2 rounded border">
                              <ul>
                                <li v-for="(highlight, hi) in result.highlights.slice(0, 2)" :key="hi" class="small">
                                  <span v-html="highlight.length > 500 ? highlight.substring(0, 500) + '...' : highlight"></span>
                                </li>
                              </ul>
                              <span class="text-muted small d-block mt-1">+{{ result.highlights.length - 2 }} more highlights</span>
                            </div>
                          </template>
                        </div>
                        <div v-if="result.url" class="mt-2">
                          <strong>Source URL:</strong>
                          <a :href="result.url" target="_blank" class="text-decoration-none small d-block mt-1 text-truncate">
                            {{ result.url }}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

            </SectionSheet>
          </template>
        </template>


        <template v-if="wls_exceeds_limit(director) && director.aml_results.wls_results && director.aml_results.wls_results.length > 0">
          <SectionSheet>
            <div class="limit-exceeded-notice mb-4">
              <div class="limit-card watchlist-limit">
                <div class="limit-header">
                  <div class="limit-icon">
                    <i class="fas fa-exclamation-triangle"></i>
                  </div>
                  <div class="limit-title">
                    <h6>Results Limit Reached</h6>
                    <span class="limit-subtitle">Watchlist Results - {{ get_director_full_name(director) }}</span>
                  </div>
                </div>
                <div class="limit-content">
                  <div class="limit-stats">
                    <div class="stat-item">
                      <span class="stat-label">Showing</span>
                      <span class="stat-value">{{ get_limited_wls_results(director).length }}</span>
                    </div>
                    <div class="stat-divider">of</div>
                    <div class="stat-item">
                      <span class="stat-label">Total</span>
                      <span class="stat-value">{{ get_wls_count(director) }}</span>
                    </div>
                  </div>
                  <div class="limit-message">
                    <i class="fas fa-info-circle"></i>
                    <span>{{ get_wls_count(director) - get_limited_wls_results(director).length }} additional results available via export</span>
                  </div>
                  <div class="limit-action">
                    <i class="fas fa-download"></i>
                    <span>Use the Export feature above to download complete data in CSV or Excel format</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="report-meta">
              <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
              <div><span class="text-muted">Director:</span> {{ index + 1 }} of {{ directors.length }}</div>
              <div><span class="text-muted">Generated:</span> {{ generated_date }}</div>
            </div>
          </SectionSheet>
        </template>

        <template v-if="!director.aml_results || !director.aml_results.wls_results || director.aml_results.wls_results.length === 0">
          <SectionSheet>
            <div class="card shadow-sm border bg-white mb-4">
              <div class="card-header">
                <h6 class="mb-0">Watchlist Results - {{ get_director_full_name(director) }}</h6>
              </div>
              <div class="card-body text-center py-4">
                <div class="alert alert-info mb-0">
                  <i class="fas fa-shield-alt fa-2x text-info mb-3"></i>
                  <h6 class="text-info">No Watchlist Records Found</h6>
                  <p class="mb-0">
                    <strong>{{ get_director_full_name(director) }}</strong> was
                    not found on any local or international watchlists or
                    sanctions databases, including but not limited to the OFAC
                    (Office of Foreign Assets Control), United Nations Security
                    Council (UNSC) sanctions lists, or any applicable Targeted
                    Financial Sanctions (TFS) lists.
                  </p>
                </div>
              </div>
            </div>
          </SectionSheet>
        </template>


        <!-- PAGE 4: System Status -->
        <SectionSheet>
          <!-- Page Header -->
          <div class="container-fluid py-3 header d-flex align-items-center justify-content-between border-bottom mb-3">
            <div class="d-flex">
              <img
                class="logo"
                width="295"
                height="125"
                src="@/assets/images/new_black_logo2.svg"
                alt="Logo"
              />
            </div>
            <div class="d-flex text-end">
              <div>
                <div class="h4 fw-bold mb-1">System Status & Database Results</div>
                <div class="text-muted text-xs">
                  Director: {{ director.director.full_name }}
                </div>
              </div>
            </div>
          </div>

          <!-- System Status Information -->
          <div class="card shadow-sm border bg-white mb-3" v-if="director.rsa_id_verification && director.rsa_id_verification.response">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="bi bi-server me-2"></i>
                System Status & Database Results
              </h6>
            </div>
            <div class="card-body">
              <!-- System Status Grid -->
              <div class="system-status-section">
                <h6 class="subsection-title">
                  <i class="bi bi-database me-2"></i>Database Status
                </h6>
                <div class="status-grid">
                  <div class="status-item">
                    <div class="status-header">
                      <i class="bi bi-database me-2"></i>
                      <span class="status-name">HANIS Database</span>
                    </div>
                    <div class="status-value">
                      <span class="badge" :class="director.rsa_id_verification.response.OnHanis ? 'bg-success' : 'bg-warning'">
                        {{ director.rsa_id_verification.response.OnHanis ? 'Found' : 'Not Found' }}
                      </span>
                    </div>
                    <div class="status-description">Home Affairs National Identification System</div>
                  </div>

                  <div class="status-item">
                    <div class="status-header">
                      <i class="bi bi-people me-2"></i>
                      <span class="status-name">NPR Database</span>
                    </div>
                    <div class="status-value">
                      <span class="badge" :class="director.rsa_id_verification.response.OnNPR ? 'bg-success' : 'bg-warning'">
                        {{ director.rsa_id_verification.response.OnNPR ? 'Found' : 'Not Found' }}
                      </span>
                    </div>
                    <div class="status-description">National Population Register</div>
                  </div>

                  <div class="status-item">
                    <div class="status-header">
                      <i class="bi bi-shield-x me-2"></i>
                      <span class="status-name">ID Blocked Status</span>
                    </div>
                    <div class="status-value">
                      <span class="badge" :class="director.rsa_id_verification.response.IDBlocked ? 'bg-danger' : 'bg-success'">
                        {{ director.rsa_id_verification.response.IDBlocked ? 'Blocked' : 'Not Blocked' }}
                      </span>
                    </div>
                    <div class="status-description">Indicates if the ID is blocked or restricted</div>
                  </div>

                  <div class="status-item">
                    <div class="status-header">
                      <i class="bi bi-heart-pulse me-2"></i>
                      <span class="status-name">Life Status</span>
                    </div>
                    <div class="status-value">
                      <span class="badge" :class="director.rsa_id_verification.response.DeadIndicator ? 'bg-danger' : 'bg-success'">
                        {{ director.rsa_id_verification.response.DeadIndicator ? 'Deceased' : 'Living' }}
                      </span>
                    </div>
                    <div class="status-description">Person's life status in Home Affairs records</div>
                  </div>

                  <div class="status-item">
                    <div class="status-header">
                      <i class="bi bi-image me-2"></i>
                      <span class="status-name">Facial Image</span>
                    </div>
                    <div class="status-value">
                      <span class="badge" :class="director.rsa_id_verification.response.FacialImageAvailable ? 'bg-success' : 'bg-secondary'">
                        {{ director.rsa_id_verification.response.FacialImageAvailable ? 'Available' : 'Not Available' }}
                      </span>
                    </div>
                    <div class="status-description">Official ID photograph availability</div>
                  </div>

                  <div class="status-item">
                    <div class="status-header">
                      <i class="bi bi-clock-history me-2"></i>
                      <span class="status-name">Result Type</span>
                    </div>
                    <div class="status-value">
                      <span class="badge bg-info">
                        {{ director.rsa_id_verification.response.CachedResult ? 'Cached' : 'Live' }}
                      </span>
                    </div>
                    <div class="status-description">
                      {{ director.rsa_id_verification.response.CachedResult ? 'Result from cache' : 'Real-time verification result' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Technical Reference -->
              <div class="technical-reference" v-if="director.rsa_id_verification.response.CRef">
                <div class="reference-card">
                  <i class="bi bi-info-circle me-2"></i>
                  <strong>Technical Reference:</strong> 
                  <code class="reference-code">{{ director.rsa_id_verification.response.CRef }}</code>
                </div>
              </div>
            </div>
          </div>

        </SectionSheet>

      </template>

            <!-- Disclaimer Section on its own page -->
      <SectionSheet>
        <div class="card shadow-sm border bg-white mb-2">
          <div class="card-header bg-light">
            <h6 class="mb-0">Disclaimer and Information</h6>
          </div>
          <div class="card-body">
            <div class="disclaimer-content">
              <p class="text-xs mb-3">
                This Director Screening Report provides comprehensive verification results for company directors
                through multiple verification channels. The screening process includes Anti-Money Laundering (AML)
                checks, watchlist verification against international databases, and identity verification through
                the South African Home Affairs National Identification System (HANIS) and National Population Register (NPR).
              </p>

              <p class="text-xs mb-3">
                <strong>Data Sources:</strong> Information is sourced from official government databases, 
                international watchlists, and compliance screening providers. AML screening includes checks 
                against sanctions lists, politically exposed persons (PEP) databases, and adverse media sources. 
                RSA ID verification data is obtained from the Department of Home Affairs.
              </p>

              <p class="text-xs mb-3">
                <strong>Compliance Notice:</strong> This report is intended to assist with Know Your Customer (KYC) 
                and due diligence requirements. It should be used in conjunction with other verification procedures 
                as part of a comprehensive compliance program.
              </p>

              <p class="text-xs mb-3">
                <strong>Privacy Notice:</strong> This report contains sensitive personal information protected under 
                the Protection of Personal Information Act (POPIA). Access should be restricted to authorized personnel 
                only. Unauthorized distribution or use of this information may result in legal action.
              </p>

              <div class="text-center text-muted mt-4">
                <p class="mb-0 text-xs">
                  © {{ new Date().getFullYear() }} The Good People Data Company. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </SectionSheet>
    </PageComponent>
  </div>
</template>

<script>
import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import Swal from 'sweetalert2';

export default {
  name: 'DirectorScreeningReport',
  components: {
    PageControl,
    PageComponent,
    SectionSheet,
  },
  props: {
    bank_account_data: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      export_options: {
        directors_summary: true,
        personal_information: false,
        aml_results: false,
        id_verification: false,
        watchlist_data: false,
        raw_json: false
      },
      export_format: 'excel',
      sheet_elements: '.sheet'
    };
  },

  mounted() {
    if (this.directors && this.directors.length > 0) {
      this.directors.forEach((director) => {
        if (director.aml_screening) {
          const aml_data = this.extract_aml_data(director.aml_screening);
          director.aml_results = {
            request_data: aml_data.RequestData || {},
            nm_results: aml_data.NmResults || [],
            pep_results: aml_data.PepResults || [],
            wls_results: aml_data.WlsResults || []
          };

          this.$forceUpdate();
        } else {

          director.aml_results = {
            request_data: {},
            nm_results: [],
            pep_results: [],
            wls_results: []
          };
        }
      });

      this.$nextTick(() => {
        this.$forceUpdate();
      });
    }
  },
  computed: {
    directors_found() {
      return this.bank_account_data.directors_found || 0;
    },
    directors_screened() {
      return this.bank_account_data.directors_screened || 0;
    },
    directors() {
      return this.bank_account_data.directors || [];
    },
    
    overall_status() {
      return this.bank_account_data.overall_status || 'Unknown';
    },
    timestamp() {
      return this.bank_account_data.timestamp;
    },
    report_reference() {
      return `DIR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    },
    generated_date() {
      return new Date().toLocaleString();
    },
    has_selected_tables() {
      return Object.values(this.export_options).some(value => value === true);
    },
    overall_status_class() {
      const status = this.overall_status.toLowerCase();
      if (status === 'passed' || status === 'success') return 'text-success';
      if (status === 'failed' || status === 'fail') return 'text-danger';
      return 'text-warning';
    },
    aml_status() {
      return this.directors_with_aml.length > 0 ? 'Failed' : 'Success';
    },
    aml_status_class() {
      const status = this.aml_status.toLowerCase();
      if (status === 'success') return 'bg-success';
      if (status === 'failed') return 'bg-danger';
      return 'bg-warning';
    },
    match_count() {
      // Count total watchlist matches across all directors
      if (this.directors && this.directors.length > 0) {
        return this.directors.reduce((count, director) => {
          const matchCount = director.aml_screening?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DRWatchlistVerification?.MatchCount || 0;
          return count + matchCount;
        }, 0);
      }
      return 0;
    },

    directors_with_aml() {
      const result = this.directors.filter(director => 
        director.aml_results && (
          director.aml_results.nm_results?.length > 0 ||
          director.aml_results.pep_results?.length > 0 ||
          director.aml_results.wls_results?.length > 0
        )
      );
      
      return result;
    },
    
    screening_decision() {
      // Determine overall screening decision based on actual match counts
      if (this.match_count === 0) {
        return 'Approve';
      } else if (this.match_count > 0) {
        return 'Review';
      }
      return 'Pending';
    },

    decision_class() {
      const decision = this.screening_decision.toLowerCase();
      if (decision === 'approve') return 'bg-success';
      if (decision === 'review') return 'bg-warning';
      return 'bg-danger';
    },
    
    response_time() {
      // Get response time from bank account data or calculate based on processing
      if (this.bank_account_data.response_time) {
        return `${this.bank_account_data.response_time}ms`;
      }
      // Default fallback
      return '2359.385ms';
    }
  },
  methods: {
    
    extract_aml_data(result) {
      const aml_result =
        result?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes
          ?.DSWatchlistVerification;
      
      var request_data = result?.RequestData || {};

      if (!aml_result) {
        return { RequestData: request_data };
      }

      const processedResult = {
        RequestData: request_data,
        NmResults: aml_result?.NmResults || [],
        PepResults: aml_result?.PepResults || [],
        WlsResults: aml_result?.WlsResults || [],
      };

      return processedResult;
    },

    get_director_subject_full_name(director) {

      if (director.aml_results?.request_data) {
        const firstName = director.aml_results.request_data.FirstName || "";
        const lastName = director.aml_results.request_data.LastName || "";
        if (firstName || lastName) {
          return `${firstName} ${lastName}`.trim();
        }
      }

      if (director.aml_results?.pep_results?.length > 0) {
        return director.aml_results.pep_results[0].name;
      } else if (director.aml_results?.nm_results?.length > 0) {
        return director.aml_results.nm_results[0].subjectMatched;
      } else if (director.aml_results?.wls_results?.length > 0) {
        return director.aml_results.wls_results[0].subjectMatched;
      }

      return this.get_director_full_name(director);
    },

    get_director_subject_date_of_birth(director) {
  
      if (director.aml_results?.request_data?.DateOfBirth) {
        const dob = director.aml_results.request_data.DateOfBirth;

        if (dob && dob.length === 8) {
          const year = dob.substring(0, 4);
          const month = dob.substring(4, 6);
          const day = dob.substring(6, 8);
          const formatted_date = new Date(`${year}-${month}-${day}`).toLocaleDateString("en-ZA");
          return formatted_date;
        }
      }


      if (director.aml_results?.pep_results?.length > 0 && director.aml_results.pep_results[0].dateOfBirth) {
        return new Date(director.aml_results.pep_results[0].dateOfBirth).toLocaleDateString("en-ZA");
      } else if (director.aml_results?.nm_results?.length > 0 && director.aml_results.nm_results[0].dateOfBirth) {
        return new Date(director.aml_results.nm_results[0].dateOfBirth).toLocaleDateString("en-ZA");
      } else if (director.aml_results?.wls_results?.length > 0 && director.aml_results.wls_results[0].dateOfBirth) {
        return new Date(director.aml_results.wls_results[0].dateOfBirth).toLocaleDateString("en-ZA");
      }
      
      return director.date_of_birth || "";
    },

    get_director_subject_gender(director) {
  
      if (director.aml_results?.request_data?.Gender) {
        const gender = director.aml_results.request_data.Gender;
        const formattedGender = gender === "M" ? "Male" : gender === "F" ? "Female" : gender;
        return formattedGender;
      }

  
      if (director.aml_results?.pep_results?.length > 0 && director.aml_results.pep_results[0].gender) {
        return director.aml_results.pep_results[0].gender;
      } else if (director.aml_results?.nm_results?.length > 0 && director.aml_results.nm_results[0].gender) {
        return director.aml_results.nm_results[0].gender;
      } else if (director.aml_results?.wls_results?.length > 0 && director.aml_results.wls_results[0].gender) {
        return director.aml_results.wls_results[0].gender;
      }
      
      return director.gender || "";
    },

    get_director_subject_country(director) {
    
      if (director.aml_results?.request_data?.CountryCode) {
        const countryCode = director.aml_results.request_data.CountryCode;
        const countryMap = {
          ZAF: "South Africa", USA: "United States", GBR: "United Kingdom", CAN: "Canada", AUS: "Australia"

        };
        const countryName = countryMap[countryCode] || countryCode;
        return countryName;
      }

      if (director.aml_results?.pep_results?.length > 0) {
        return director.aml_results.pep_results[0].country;
      } else if (director.aml_results?.nm_results?.length > 0) {
        return director.aml_results.nm_results[0].sourceCountry;
      } else if (director.aml_results?.wls_results?.length > 0) {
        return director.aml_results.wls_results[0].sourceCountry;
      }
      
      return "South Africa"; // Default
    },

    get_limited_nm_results(director) {
      const results = director.aml_results?.nm_results || [];
      return results.slice(0, 11); 
    },
    
    get_limited_pep_results(director) {
      const results = director.aml_results?.pep_results || [];
      return results.slice(0, 11);
    },
    
    get_limited_wls_results(director) {
      const results = director.aml_results?.wls_results || [];
      return results.slice(0, 11);
    },

    nm_exceeds_limit(director) {
      return director.aml
    },
    
    pep_exceeds_limit(director) {
      const results = director.aml_results?.pep_results || [];
      return results.length > 11; 
    },
    
    wls_exceeds_limit(director) {
      const results = director.aml_results?.wls_results || [];
      return results.length > 11;
    },

   
    get_pep_result_pages(director) {
      const results = this.get_limited_pep_results(director);
      const pages = [];
      const itemsPerPage = 2;
      
      for (let i = 0; i < results.length; i += itemsPerPage) {
        pages.push({
          results: results.slice(i, i + itemsPerPage),
          isFirst: i === 0
        });
      }
      
      return pages;
    },

   
    get_director_full_name(director) {
      return `${director.first_name || ''} ${director.last_name || ''}`.trim();
    },

    
    get_nm_count(director) {
      return director.aml_results?.nm_results?.length || 0;
    },
    
    get_pep_count(director) {
      return director.aml_results?.pep_results?.length || 0;
    },
    
    get_wls_count(director) {
      return director.aml_results?.wls_results?.length || 0;
    },

    
    get_total_matches(director) {
      return this.get_nm_count(director) + this.get_pep_count(director) + this.get_wls_count(director);
    },
    
    // Page calculation methods
    totalPagesPerDirector() {
      // Enhanced pages: Overview (1) + ID Verification (1) + AML Screening (1) + System Status (1) + Verification Results (1) = 5
      return 5;
    },
    
    getTotalPages() {
      return this.directors.length * this.totalPagesPerDirector();
    },
    
    // Helper to get page number for a specific director and page type
    getPageNumber(directorIndex, pageType) {
      const basePageNum = directorIndex * this.totalPagesPerDirector();
      switch(pageType) {
        case 'overview': return basePageNum + 1;
        case 'id_verification': return basePageNum + 2;
        case 'aml_screening': return basePageNum + 3;
        case 'system_status': return basePageNum + 4;
        case 'verification_results': return basePageNum + 5;
        default: return basePageNum + 1;
      }
    },
    
    // Helper to check if content should split across pages
    shouldSplitContent(director) {
      // Logic to determine if content is too long for single page
      const hasExtensiveAMLData = director.aml_screening?.response?.watchlists?.length > 5;
      const hasMultipleVerificationSteps = director.rsa_id_verification?.response;
      const hasLargeSystemStatus = Object.keys(director.rsa_id_verification?.response || {}).length > 10;
      
      return hasExtensiveAMLData || hasMultipleVerificationSteps || hasLargeSystemStatus;
    },

    // Export Functions
    show_export_modal() {
      const modal = document.getElementById(`export-modal-${this.report_reference}`);
      if (modal) {
        modal.style.display = 'block';
        modal.classList.add('show');
        document.body.classList.add('modal-open');
      }
    },
    close_modal() {
      const modal = document.getElementById(`export-modal-${this.report_reference}`);
      if (modal) {
        modal.style.display = 'none';
        modal.classList.remove('show');
        document.body.classList.remove('modal-open');
      }
    },
    export_data() {
      if (!this.has_selected_tables) {
        Swal.fire({
          icon: "warning",
          title: "No Data Selected",
          text: "Please select at least one data section to export.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return;
      }

      try {
        if (this.export_format === "csv") {
          this.export_csv();
        } else if (this.export_format === "excel") {
          this.export_excel();
        } else if (this.export_format === "json") {
          this.export_json();
        }
        this.close_modal();
      } catch (error) {
        console.error("Export error:", error);
        Swal.fire({
          icon: "error",
          title: "Export Failed",
          text: "There was a problem exporting your data. Please try again.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },

    async export_excel() {
      try {
        await this.load_export_libraries();
        
        // eslint-disable-next-line no-undef
        const workbook = XLSX.utils.book_new();
        const current_date = this.get_current_date_string();

        // Export Directors Summary
        if (this.export_options.directors_summary) {
          const summary_data = this.prepare_directors_summary_data();
          // eslint-disable-next-line no-undef
          const worksheet = XLSX.utils.json_to_sheet(summary_data);
          // eslint-disable-next-line no-undef
          XLSX.utils.book_append_sheet(workbook, worksheet, "Directors Summary");
        }

        // Export Personal Information
        if (this.export_options.personal_information) {
          const personal_data = this.prepare_personal_information_data();
          // eslint-disable-next-line no-undef
          const worksheet = XLSX.utils.json_to_sheet(personal_data);
          // eslint-disable-next-line no-undef
          XLSX.utils.book_append_sheet(workbook, worksheet, "Personal Information");
        }

        // Export AML Results
        if (this.export_options.aml_results) {
          const aml_data = this.prepare_aml_results_data();
          // eslint-disable-next-line no-undef
          const worksheet = XLSX.utils.json_to_sheet(aml_data);
          // eslint-disable-next-line no-undef
          XLSX.utils.book_append_sheet(workbook, worksheet, "AML Results");
        }

        // Export ID Verification Results
        if (this.export_options.id_verification) {
          const id_data = this.prepare_id_verification_data();
          // eslint-disable-next-line no-undef
          const worksheet = XLSX.utils.json_to_sheet(id_data);
          // eslint-disable-next-line no-undef
          XLSX.utils.book_append_sheet(workbook, worksheet, "ID Verification");
        }

        // Export Watchlist Data
        if (this.export_options.watchlist_data) {
          const watchlist_data = this.prepare_watchlist_data();
          // eslint-disable-next-line no-undef
          const worksheet = XLSX.utils.json_to_sheet(watchlist_data);
          // eslint-disable-next-line no-undef
          XLSX.utils.book_append_sheet(workbook, worksheet, "Watchlist Data");
        }

        // Export Raw JSON Data
        if (this.export_options.raw_json) {
          const raw_data = this.prepare_raw_json_data();
          // eslint-disable-next-line no-undef
          const worksheet = XLSX.utils.json_to_sheet([raw_data]);
          // eslint-disable-next-line no-undef
          XLSX.utils.book_append_sheet(workbook, worksheet, "Raw Data");
        }

        const filename = `Director_Screening_Report_${current_date}.xlsx`;
        // eslint-disable-next-line no-undef
        XLSX.writeFile(workbook, filename);

        Swal.fire({
          icon: "success",
          title: "Export Successful",
          text: `Data exported successfully as ${filename}`,
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Excel export error:", error);
        throw error;
      }
    },

    async export_csv() {
      try {
        await this.load_export_libraries();
        
        const current_date = this.get_current_date_string();
        let csv_content = "";

        if (this.export_options.directors_summary) {
          const summary_data = this.prepare_directors_summary_data();
          csv_content += "Directors Summary\n";
          // eslint-disable-next-line no-undef
          csv_content += Papa.unparse(summary_data) + "\n\n";
        }

        if (this.export_options.personal_information) {
          const personal_data = this.prepare_personal_information_data();
          csv_content += "Personal Information\n";
          // eslint-disable-next-line no-undef
          csv_content += Papa.unparse(personal_data) + "\n\n";
        }

        if (this.export_options.aml_results) {
          const aml_data = this.prepare_aml_results_data();
          csv_content += "AML Results\n";
          // eslint-disable-next-line no-undef
          csv_content += Papa.unparse(aml_data) + "\n\n";
        }

        if (this.export_options.id_verification) {
          const id_data = this.prepare_id_verification_data();
          csv_content += "ID Verification\n";
          // eslint-disable-next-line no-undef
          csv_content += Papa.unparse(id_data) + "\n\n";
        }

        if (this.export_options.watchlist_data) {
          const watchlist_data = this.prepare_watchlist_data();
          csv_content += "Watchlist Data\n";
          // eslint-disable-next-line no-undef
          csv_content += Papa.unparse(watchlist_data) + "\n\n";
        }

        const blob = new Blob([csv_content], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        
        link.setAttribute("href", url);
        link.setAttribute("download", `Director_Screening_Report_${current_date}.csv`);
        link.style.visibility = "hidden";
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        Swal.fire({
          icon: "success",
          title: "Export Successful",
          text: `Data exported successfully as CSV`,
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("CSV export error:", error);
        throw error;
      }
    },

    async export_json() {
      try {
        const current_date = this.get_current_date_string();
        const export_data = this.prepare_export_json_data();
        
        const json_content = JSON.stringify(export_data, null, 2);
        const blob = new Blob([json_content], { type: "application/json;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        
        link.setAttribute("href", url);
        link.setAttribute("download", `Director_Screening_Report_${current_date}.json`);
        link.style.visibility = "hidden";
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        Swal.fire({
          icon: "success",
          title: "Export Successful",
          text: `Data exported successfully as JSON`,
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("JSON export error:", error);
        throw error;
      }
    },

    prepare_directors_summary_data() {
      return this.directors.map((director, index) => ({
        "Director #": index + 1,
        "Full Name": this.get_director_subject_full_name(director),
        "ID Number": director.director?.id_number || "N/A",
        "Status": director.director?.status || "N/A",
        "Risk Level": this.calculateRiskLevel(director),
        "AML Matches": this.getTotalAMLMatches(director),
        "ID Verification Status": this.getCleanVerificationStatus(director.rsa_id_verification?.response?.Status, director),
        "Overall Decision": this.getWatchlistDecision(director)
      }));
    },

    prepare_personal_information_data() {
      return this.directors.map((director, index) => ({
        "Director #": index + 1,
        "First Name": this.getDirectorFirstName(director),
        "Last Name": this.getDirectorLastName(director),
        "Full Name": this.get_director_subject_full_name(director),
        "Date of Birth": this.get_director_subject_date_of_birth(director),
        "Gender": this.get_director_subject_gender(director),
        "Country": this.get_director_subject_country(director),
        "ID Number": director.director?.id_number || "N/A",
        "Status": director.director?.status || "Active"
      }));
    },

    prepare_aml_results_data() {
      const aml_data = [];

      this.directors.forEach((director, index) => {
        const watchlist_verification = director.aml_screening?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DSWatchlistVerification;

        if (watchlist_verification) {
          const nm_count = watchlist_verification.NmResults?.length || 0;
          const wls_count = watchlist_verification.WlsResults?.length || 0;
          const pep_count = watchlist_verification.PepResults?.length || 0;

          aml_data.push({
            "Director #": index + 1,
            "Full Name": this.get_director_subject_full_name(director),
            "Total Matches": nm_count + wls_count + pep_count,
            "NM Results": nm_count,
            "PEP Results": pep_count,
            "Watchlist Results": wls_count,
            "Decision": watchlist_verification.WatchlistDecision || "Unknown",
            "Risk Level": this.calculateRiskLevel(director)
          });

          // NM results
          if (watchlist_verification.NmResults?.length > 0) {
            watchlist_verification.NmResults.forEach((nm, nmIndex) => {
              aml_data.push({
                "Director #": `${index + 1} - NM ${nmIndex + 1}`,
                "Type": "Negative Media",
                "Subject Matched": nm.subjectMatched || "N/A",
                "Score": nm.score || "N/A",
                "Category": nm.category || "N/A",
                "Source Country": nm.sourceCountry || "N/A",
                "Article Title": nm.articleTitle || "N/A",
                "Article Date": nm.articleDate || "N/A",
                "URL": nm.url || "N/A"
              });
            });
          }

          // PEP results
          if (watchlist_verification.PepResults?.length > 0) {
            watchlist_verification.PepResults.forEach((pep, pepIndex) => {
              aml_data.push({
                "Director #": `${index + 1} - PEP ${pepIndex + 1}`,
                "Type": "PEP",
                "Name": pep.name || "N/A",
                "Score": pep.score || "N/A",
                "Country": pep.country || pep.countryCode || "N/A",
                "Date of Birth": pep.dateOfBirth || "N/A",
                "Gender": pep.gender || "N/A",
                "Professional History": Array.isArray(pep.professionalHistory) 
                  ? pep.professionalHistory.join("; ") 
                  : (pep.professionalHistory || "N/A"),
                "Member Of": Array.isArray(pep.memberOf) 
                  ? pep.memberOf.join("; ") 
                  : (pep.memberOf || "N/A"),
                "Source URLs": Array.isArray(pep.sourceInfoURL) 
                  ? pep.sourceInfoURL.join("; ") 
                  : (pep.sourceInfoURL || "N/A")
              });
            });
          }

          // WLS results
          if (watchlist_verification.WlsResults?.length > 0) {
            watchlist_verification.WlsResults.forEach((wls, wlsIndex) => {
              aml_data.push({
                "Director #": `${index + 1} - WLS ${wlsIndex + 1}`,
                "Type": "Watchlist",
                "Subject Matched": wls.subjectMatched || "N/A",
                "Score": wls.score || "N/A",
                "Category": wls.category || "N/A",
                "Source Country": wls.sourceCountry || "N/A",
                "Source Agency": wls.sourceAgencyName || "N/A",
                "Source List Type": wls.sourceListType || "N/A",
                "Remarks": wls.remarks || "N/A"
              });
            });
          }
        } else {
          // No AML data available
          aml_data.push({
            "Director #": index + 1,
            "Full Name": this.get_director_subject_full_name(director),
            "Total Matches": 0,
            "NM Results": 0,
            "PEP Results": 0,
            "Watchlist Results": 0,
            "Decision": "No Data",
            "Risk Level": this.calculateRiskLevel(director)
          });
        }
      });
      return aml_data;
    },

    prepare_id_verification_data() {
      return this.directors.map((director, index) => ({
        "Director #": index + 1,
        "Full Name": this.get_director_subject_full_name(director),
        "ID Number": director.director?.id_number || "N/A",
        "Status": this.getCleanVerificationStatus(director.rsa_id_verification?.response?.Status, director),
        "First Name Match": director.rsa_id_verification?.response?.FirstNameResult || "N/A",
        "Last Name Match": director.rsa_id_verification?.response?.LastNameResult || "N/A",
        "Average Match": this.getAverageNameMatch(director) + "%",
        "ID Blocked": director.rsa_id_verification?.response?.IDBlocked ? "Yes" : "No",
        "Dead Indicator": director.rsa_id_verification?.response?.DeadIndicator ? "Yes" : "No",
        "On HANIS": director.rsa_id_verification?.response?.OnHanis ? "Yes" : "No",
        "On NPR": director.rsa_id_verification?.response?.OnNPR ? "Yes" : "No",
        "Risk Level": this.calculateRiskLevel(director)
      }));
    },

    prepare_watchlist_data() {
      const watchlist_data = [];
      
      this.directors.forEach((director, index) => {
        const watchlist_info = this.getWatchlistData(director);
        
        watchlist_data.push({
          "Director #": index + 1,
          "Full Name": this.get_director_subject_full_name(director),
          "Total Watchlists": watchlist_info.total,
          "Pending Count": watchlist_info.pending,
          "Excluded Count": watchlist_info.excluded,
          "Match Count": this.getWatchlistMatchCount(director),
          "Decision": this.getWatchlistDecision(director),
          "Response Time": this.formatResponseTime(watchlist_info.APIResponseTime)
        });
      });

      return watchlist_data;
    },

    prepare_export_json_data() {
      const export_data = {
        report_metadata: {
          report_reference: this.report_reference,
          generated_date: this.generated_date,
          export_timestamp: new Date().toISOString(),
          total_directors: this.directors_found,
          directors_screened: this.directors_screened,
          overall_status: this.overall_status
        }
      };

      if (this.export_options.directors_summary) {
        export_data.directors_summary = this.prepare_directors_summary_data();
      }

      if (this.export_options.personal_information) {
        export_data.personal_information = this.prepare_personal_information_data();
      }

      if (this.export_options.aml_results) {
        export_data.aml_results = this.prepare_aml_results_data();
      }

      if (this.export_options.id_verification) {
        export_data.id_verification = this.prepare_id_verification_data();
      }

      if (this.export_options.watchlist_data) {
        export_data.watchlist_data = this.prepare_watchlist_data();
      }

      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }

      return export_data;
    },

    prepare_raw_json_data() {
      return {
        bank_account_data: this.bank_account_data,
        directors: this.directors,
        processing_metadata: {
          timestamp: this.timestamp,
          response_time: this.response_time,
          report_reference: this.report_reference
        }
      };
    },

    async load_export_libraries() {
      // eslint-disable-next-line no-undef
      if (typeof XLSX === 'undefined') {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js');
      }
      // eslint-disable-next-line no-undef
      if (typeof Papa === 'undefined') {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.2/papaparse.min.js');
      }
    },

    load_script(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    get_current_date_string() {
      const now = new Date();
      return now.toISOString().split('T')[0].replace(/-/g, '');
    },

    downloadDirectorReport(director) {

      alert(`Download functionality for ${director.director.full_name} will be implemented`);
    },

    // Status Badge Classes
    getStatusBadgeClass(status) {
      const classes = {
        'Active': 'text-success',
        'Inactive': 'text-secondary',
        'Suspended': 'text-warning'
      };
      return classes[status] || 'text-muted';
    },
    getStatusTextClass(status) {
      const classes = {
        'Active': 'text-success fw-bold',
        'Inactive': 'text-secondary',
        'Suspended': 'text-warning fw-bold'
      };
      return classes[status] || 'text-muted';
    },
    getIDVerificationBadgeClass(status) {
      // Handle specific cases for deceased and smart card
      if (status === 'Deceased') return 'bg-danger';
      if (status === 'ID Blocked') return 'bg-warning';
      
      // Handle standard status types
      const successStatuses = ['Success', 'Verified'];
      const errorStatuses = ['Failed', 'Error'];
      
      if (successStatuses.includes(status)) return 'bg-success';
      if (errorStatuses.includes(status)) return 'bg-danger';
      
      return 'bg-warning'; // Default for pending or unknown status
    },
    getMatchResultBadge(percentage) {
      const percent = parseFloat(percentage || 0);
      if (percent >= 90) return 'bg-success';
      if (percent >= 70) return 'bg-warning';
      return 'bg-danger';
    },
    getAverageNameMatch(director) {
      if (director.rsa_id_verification?.response) {
        const first = parseFloat(director.rsa_id_verification.response.FirstNameResult || 0);
        const last = parseFloat(director.rsa_id_verification.response.LastNameResult || 0);
        return Math.round((first + last) / 2);
      }
      return 0;
    },
    calculateRiskLevel(director) {
      let riskScore = 0;
      
      // Check AML screening
      if (director.aml_screening?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DRWatchlistVerification) {
        const matchCount = parseInt(director.aml_screening.Fields.Applicants_IO.Applicant[0].Attributes.DRWatchlistVerification.MatchCount || 0);
        if (matchCount > 0) riskScore += 30;
      }
      
      // Check ID verification
      if (director.rsa_id_verification?.response) {
        if (director.rsa_id_verification.response.IDBlocked) riskScore += 40;
        if (director.rsa_id_verification.response.DeadIndicator) riskScore += 50;
        if (!director.rsa_id_verification.response.OnHanis) riskScore += 20;
        if (!director.rsa_id_verification.response.OnNPR) riskScore += 15;
        
        const avgMatch = this.getAverageNameMatch(director);
        if (avgMatch < 70) riskScore += 25;
        else if (avgMatch < 90) riskScore += 10;
      }
      
      if (riskScore >= 50) return 'High';
      if (riskScore >= 25) return 'Medium';
      return 'Low';
    },
    getCleanVerificationStatus(status, director = null) {
      // Check deceased status and smart card based on your specific cases
      if (director && director.rsa_id_verification && director.rsa_id_verification.response) {
        const response = director.rsa_id_verification.response;
        
        // Case 1: If person is deceased
        if (response.DeadIndicator === true) {
          return 'Deceased';
        }

        // Case 2: If ID is blocked
        if (response.IDBlocked === true) {
          return 'ID Blocked';
        }
      }
      
      // Default for all other cases - don't return complex status messages
      return 'Verified';
    },
    getCleanAMLStatus(status) {
      if (!status) return 'Pending';
      
      if (status.includes('Success')) return 'Success';
      if (status.includes('Complete')) return 'Complete';
      if (status.includes('Failed')) return 'Failed';
      if (status.includes('Error')) return 'Error';
      
      return status;
    },
    getRiskLevelClass(director) {
      const riskLevel = this.calculateRiskLevel(director);
      if (riskLevel === 'High') return 'bg-danger';
      if (riskLevel === 'Medium') return 'bg-warning';
      return 'bg-success';
    },
    formatResponseTime(time) {
      if (!time) return 'N/A';
      
      // Convert to number if it's a string
      const numTime = parseFloat(time);
      
      // Format the response time nicely
      if (numTime >= 1000) {
        return `${(numTime / 1000).toFixed(2)}s`;
      } else {
        return `${numTime.toFixed(0)}ms`;
      }
    },

    // Helper method to get watchlist data
    getWatchlistData(director) {
      if (!director.aml_screening?.response) {
        return { total: 0, pending: 0, excluded: 0, APIResponseTime: null };
      }
      
      const response = director.aml_screening.response;
      return {
        total: response.watchlists_total_count || 0,
        pending: response.watchlists_pending_count || 0,
        excluded: response.watchlists_excluded_count || 0,
        APIResponseTime: response.APIResponseTime || null
      };
    },

    // Helper method to get AML status class
    getAMLStatusClass(status) {
      if (!status) return 'secondary';
      
      const cleanStatus = status.toString().toLowerCase();
      if (cleanStatus.includes('success') || cleanStatus.includes('clear')) {
        return 'success';
      } else if (cleanStatus.includes('pending') || cleanStatus.includes('review')) {
        return 'warning';
      } else if (cleanStatus.includes('risk') || cleanStatus.includes('fail') || cleanStatus.includes('alert')) {
        return 'danger';
      }
      return 'secondary';
    },

    // Helper method to get pass/fail status from percentage
    getPassFailStatus(percentage) {
      const value = parseFloat(percentage || 0);
      return value >= 80 ? 'Pass' : 'Fail';
    },

    // Helper method to get pass/fail variant from percentage
    getPassFailVariant(percentage) {
      const value = parseFloat(percentage || 0);
      return value >= 80 ? 'success' : 'danger';
    },

    // Helper method to get overall pass/fail status
    getOverallPassFailStatus(status) {
      if (!status) return 'Fail';
      const cleanStatus = status.toString().toLowerCase();
      return ['success', 'verified'].includes(cleanStatus) ? 'Pass' : 'Fail';
    },

    // Helper method to get overall pass/fail variant
    getOverallPassFailVariant(status) {
      if (!status) return 'danger';
      const cleanStatus = status.toString().toLowerCase();
      return ['success', 'verified'].includes(cleanStatus) ? 'success' : 'danger';
    },

    // Helper method to get ID verification variant for badges
    getIDVerificationVariant(status) {
      if (!status) return 'secondary';
      
      const cleanStatus = status.toString().toLowerCase();
      if (cleanStatus.includes('success') || cleanStatus.includes('verified') || cleanStatus.includes('pass')) {
        return 'success';
      } else if (cleanStatus.includes('pending') || cleanStatus.includes('review') || cleanStatus.includes('warning')) {
        return 'warning';
      } else if (cleanStatus.includes('fail') || cleanStatus.includes('error') || cleanStatus.includes('denied')) {
        return 'danger';
      }
      return 'secondary';
    },

    // Date Formatting
    formatDateTime(dateString) {
            if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleString();
      } catch (e) {
        return dateString;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString();
      } catch (e) {
        return dateString;
      }
    },
    formatDateOfBirth(dateString) {
      if (!dateString) return 'N/A';
      // Handle YYYYMMDD format
      if (dateString.length === 8) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        return `${day}/${month}/${year}`;
      }
      return dateString;
    },
    formatIDDate(dateString) {
      if (!dateString) return 'N/A';
      // Handle YYYYMMDD format
      if (dateString.length === 8) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        return `${day}/${month}/${year}`;
      }
      return dateString;
    },
    formatGender(gender) {
      const genderMap = {
        'M': 'Male',
        'F': 'Female'
      };
      return genderMap[gender] || gender;
    },
    formatCountryCode(code) {
      const countryMap = {
        'ZAF': 'South Africa',
        'USA': 'United States',
        'GBR': 'United Kingdom'
      };
      return countryMap[code] || code;
    },

    // Image Handling
    hasValidBase64Image(facialImage) {
      if (!facialImage || facialImage === 'base64' || facialImage === '') {
        return false;
      }
      
      try {
        if (facialImage.startsWith('data:image/')) {
          return true;
        }
        
        const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
        return facialImage.length > 20 && facialImage.length % 4 === 0 && base64Regex.test(facialImage);
      } catch (e) {
        return false;
      }
    },
    getBase64ImageSrc(facialImage) {
      if (!facialImage) return '';
      
      if (facialImage.startsWith('data:image/')) {
        return facialImage;
      }
      
      return `data:image/jpeg;base64,${facialImage}`;
    },
    handleImageError(event) {
      console.warn('Failed to load facial image:', event);
    },

    // AML Data Helper Methods
    getTotalAMLMatches(director) {
      // Since detailed results aren't available, use the match count from DRWatchlistVerification
      return this.getWatchlistMatchCount(director);
    },

    getWatchlistMatchCount(director) {
      return director.aml_screening?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DRWatchlistVerification?.MatchCount || 0;
    },

    getWatchlistDecision(director) {
      return director.aml_screening?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DRWatchlistVerification?.WatchlistDecision || 'Unknown';
    },


    getDirectorFirstName(director) {
      return director.aml_screening?.RequestData?.FirstName || 
             director.director?.first_name || 
             (director.director?.full_name ? director.director.full_name.split(' ')[0] : '');
    },

    getDirectorLastName(director) {
      return director.aml_screening?.RequestData?.LastName || 
             director.director?.last_name || 
             (director.director?.full_name ? director.director.full_name.split(' ').slice(1).join(' ') : '');
    },

    getDirectorDateOfBirth(director) {
      if (director.aml_screening?.RequestData?.DateOfDirth) {
        const dob = director.aml_screening.RequestData.DateOfDirth;
        if (dob && dob.length === 8) {
          const year = dob.substring(0, 4);
          const month = dob.substring(4, 6);
          const day = dob.substring(6, 8);
          return new Date(`${year}-${month}-${day}`).toLocaleDateString("en-ZA");
        }
      }
      return director.director?.date_of_birth || '';
    },

    getDirectorGender(director) {
      if (director.aml_screening?.RequestData?.Gender) {
        const gender = director.aml_screening.RequestData.Gender;
        return gender === "M" ? "Male" : gender === "F" ? "Female" : gender;
      }
      return director.director?.gender || '';
    },

    getDirectorCountry(director) {
      if (director.aml_screening?.RequestData?.CountryCode) {
        return this.formatCountryCode(director.aml_screening.RequestData.CountryCode);
      }
      return director.director?.country || 'South Africa';
    },
  }
};
</script>

<style scoped>
.sheet {
  padding: 10mm !important;
}

#director-screening-report {
  font-family: "Arial", sans-serif;
  font-size: 12px;
}

.card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.2s ease-in-out;
  overflow: hidden;
}

.card-header {
  background: white !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header h6 {
  margin: 0 0 0 -16px;
  padding: 0 0 0 20px;
  border-left: 3px solid #59ff74;
  font-weight: 600;
  line-height: 1.5;
  font-size: 1rem;
}

.card-body {
  padding: 1.25rem !important;
}

.report-meta {
  position: relative;
  bottom: auto;
  left: auto;
  right: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2rem 0 1rem 0;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 8px;
  font-size: 0.75rem;
  color: #495057;
  font-weight: 500;
  min-height: 60px;
  width: 100%;
  box-sizing: border-box;
}

/* Card styling for Director results */
.border-left-accent {
  border-left: 3px solid #59ff74 !important;
}

.director-card.border-left-accent {
  border-left-color: #036016 !important;
}

.aml-card.border-left-accent {
  border-left-color: #007313 !important;
}

.id-verification-card.border-left-accent {
  border-left-color: #358826 !important;
}

.result-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Section specific styling */
.director-result-card {
  border-radius: 6px;
  padding: 0.75rem;
  margin: -0.5rem;
}

.aml-result-card {
  border-radius: 6px;
  padding: 0.75rem;
  margin: -0.5rem;
}

.id-result-card {
  border-radius: 6px;
  padding: 0.75rem;
  margin: -0.5rem;
}

/* Enhanced Status Cards for Summary */
.status-card-enhanced {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  height: 100%;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.status-card-enhanced:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}

.status-icon-enhanced {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: white;
  font-size: 1.5rem;
  position: relative;
}

.status-icon-enhanced::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, rgba(255,255,255,0.3), transparent);
  border-radius: 50%;
  z-index: 1;
}

.status-content-enhanced {
  position: relative;
  z-index: 2;
}

.status-label-enhanced {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.status-value-enhanced {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.25rem;
  line-height: 1;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.status-detail-enhanced {
  font-size: 0.7rem;
  color: #6c757d;
  font-weight: 500;
}

/* Page Content Management and Overflow Prevention */
.sheet {
  min-height: auto;
  max-height: none;
  padding: 15mm 20mm !important;
}

.card {
  margin-bottom: 20px;
  break-inside: avoid;
}

.table-responsive {
  margin-bottom: 1rem;
}

.table {
  font-size: 12px;
}

.table td, .table th {
  padding: 6px 10px;
  vertical-align: middle;
}

@media print {
  .sheet {
    page-break-after: always;
    min-height: auto;
    height: auto;
    overflow: visible;
  }
  
  .card {
    break-inside: avoid;
  }
  
  .table {
    break-inside: avoid;
  }
}

/* Enhanced Information Cards */
.info-card-enhanced {
  display: flex;
  align-items: flex-start;
  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
  border: 1px solid #e3e6f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  height: 100%;
}

.info-card-enhanced:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  border-color: #4e73df;
}

.info-icon-enhanced {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.info-icon-enhanced i {
  font-size: 18px;
  color: white;
}

.info-content-enhanced {
  flex: 1;
  min-width: 0;
}

.info-label-enhanced {
  font-size: 12px;
  font-weight: 600;
  color: #5a5c69;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.info-value-enhanced {
  font-size: 14px;
  font-weight: 600;
  color: #1e2139;
  line-height: 1.4;
  word-break: break-word;
}

.info-match-enhanced {
  margin-top: 6px;
}

.match-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.badge-high {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.badge-medium {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.badge-low {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* Enhanced Photo Section */
.photo-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 8px;
  padding: 1.25rem;
  text-align: center;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.photo-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.photo-frame {
  width: 200px;
  height: 250px;
  border: 3px solid #dee2e6;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  position: relative;
}

.photo-frame::before {
  content: '';
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  background: linear-gradient(45deg, #59ff74, #46e061, #59ff74);
  border-radius: 12px;
  z-index: -1;
}

.official-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  transition: transform 0.3s ease;
}

.official-photo:hover {
  transform: scale(1.05);
}

.photo-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  height: 100%;
}

.photo-placeholder i {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
}

.photo-placeholder p {
  margin: 0;
  font-weight: 600;
  font-size: 0.9rem;
}

.photo-placeholder small {
  font-size: 0.75rem;
  opacity: 0.7;
}

.photo-caption {
  background: rgba(89, 255, 116, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: 1px solid rgba(89, 255, 116, 0.3);
}

/* Enhanced Analysis Cards - matching AML report exactly */
.analysis-card {
  background: #ffffff;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  height: 100%;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.analysis-icon {
  font-size: 1.5rem;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
  color: #fff;
}

.analysis-icon.aml-status { background: #17a2b8; }
.analysis-icon.confidence { background: #28a745; }
.analysis-icon.quality { background: #20c997; }
.analysis-icon.assessment { background: #6f42c1; }
.analysis-icon.response-time { background: #fd7e14; }
.analysis-icon.risk-level { background: #dc3545; }
.analysis-icon.watchlist { background: #007bff; }
.analysis-icon.decision { background: #ffc107; color: #000; }

.analysis-content {
  flex: 1;
  min-width: 0;
}

.analysis-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.analysis-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #212529;
  margin-bottom: 0.25rem;
  line-height: 1.2;
}

.analysis-detail {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
}

/* Additional styling for enhanced content - matching AML report */
.badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  font-weight: 500;
  border-radius: 4px;
}

.card-body p {
  margin-bottom: 0.5rem;
}

.card-body ul {
  padding-left: 1.2rem;
}

.card-body ul li {
  margin-bottom: 0.25rem;
}

.border-top {
  border-top: 1px solid #dee2e6 !important;
}

/* Highlight emphasis styling */
em {
  background-color: #fff3cd;
  padding: 0.1rem 0.2rem;
  border-radius: 0.2rem;
  font-style: normal;
  font-weight: 600;
}

/* URL truncation for better layout */
.text-decoration-none {
  text-decoration: none !important;
  color: #0d6efd;
}

.text-decoration-none:hover {
  text-decoration: underline !important;
  color: #0a58ca;
}

/* Strong text styling */
strong {
  font-weight: 600;
  color: #495057;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f4;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: #555;
  min-width: 150px;
}

.info-value {
  color: #333;
  word-break: break-word;
}

.image-container {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 10px;
  background: #f8f9fa;
  text-align: center;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-container img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 4px;
}

/* Badge styles - matching AML report */
.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  color: white !important;
  font-size: 10px;
  color: white !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.status-passed {
  background: #4CAF50;
  color: white;
}

.status-badge.status-failed {
  background: #f44336;
  color: white;
}

.status-badge.status-warning {
  background: #ff9800;
  color: white;
}

.status-passed {
  background: #4CAF50;
  color: white;
  border-radius: 12px;
  padding: 4px 12px;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-failed {
  background: #f44336;
  color: white;
  border-radius: 12px;
  padding: 4px 12px;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-warning {
  background: #ff9800;
  color: white;
  border-radius: 12px;
  padding: 4px 12px;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Alert styling - matching AML report */
.alert {
  border-radius: 6px !important;
  font-size: 11px !important;
  padding: 10px 12px !important;
  margin-bottom: 10px !important;
}

.alert-info {
  background-color: #d1ecf1 !important;
  border-color: #bee5eb !important;
  color: #0c5460 !important;
  border-radius: 6px;
}

.fa-info-circle {
  opacity: 0.8;
}

/* Logo styling */
.logo {
  max-width: 200px;
  height: auto;
}

/* Table enhancements - matching AML report */
.table-responsive {
  border-radius: 8px;
  overflow: hidden;
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-light {
  background-color: #f8f9fa;
}

.table {
  font-size: 11px !important;
  margin-bottom: 0 !important;
}

.table thead th {
  background-color: #f8f9fa !important;
  border-bottom: 2px solid #e9ecef !important;
  font-weight: 600 !important;
  font-size: 10px !important;
  padding: 8px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  color: #495057 !important;
}

.table tbody td {
  padding: 8px !important;
  border-top: 1px solid #e9ecef !important;
  vertical-align: middle !important;
}

/* Director header styling */
.director-header {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  margin-bottom: 20px !important;
  padding-bottom: 10px !important;
  border-bottom: 1px solid #e9ecef !important;
}

.director-header h5 {
  color: #2c3e50 !important;
  font-weight: 600 !important;
  margin: 0 !important;
  font-size: 18px !important;
}

.page-indicator {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  padding: 6px 16px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
  margin: 0 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 80px;
  text-align: center;
  white-space: nowrap;
  display: inline-block;
}

/* Download button styling - matching AML report */
.btn-bg-green {
  background-color: #59ff74 !important;
  border: none !important;
  color: #000 !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  padding: 6px 12px !important;
  border-radius: 4px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.btn-bg-green:hover {
  background-color: #46e061 !important;
  color: #000 !important;
}

/* Image styling - matching AML report */
.verification-image {
  max-width: 100% !important;
  height: auto !important;
  border-radius: 6px !important;
  border: 2px solid #e9ecef !important;
}

.no-image {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 150px !important;
  background-color: #f8f9fa !important;
  border: 2px dashed #dee2e6 !important;
  border-radius: 6px !important;
  color: #6c757d !important;
  font-size: 11px !important;
}

.no-image i {
  font-size: 24px !important;
  margin-bottom: 8px !important;
  opacity: 0.5 !important;
}

/* Page break control - matching AML report */
.SectionSheet {
  page-break-after: always !important;
  position: relative !important;
  min-height: auto !important;
  height: auto !important;
  overflow: visible !important;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* Content overflow management */
.card {
  break-inside: avoid;
  margin-bottom: 1rem;
}

.card-body {
  overflow: visible;
}

.metrics-grid, .status-grid, .verification-grid {
  break-inside: avoid;
}

.metric-card, .status-item, .verification-item {
  break-inside: avoid;
  margin-bottom: 0.75rem;
}

/* Ensure footers display properly */
.report-meta {
  margin-top: auto;
  padding-top: 1rem;
  position: relative;
  width: 100%;
  clear: both;
}

/* Print styles - matching AML report */
@media print {
  .btn,
  .modal,
  .PageControl {
    display: none !important;
  }
  
  .sheet {
    box-shadow: none !important;
    margin: 0 !important;
    padding: 20mm !important;
  }
  
  .card {
    box-shadow: none !important;
  }
  
  body {
    margin: 0 !important;
    padding: 0 !important;
  }
}

/* Ensure proper A4 formatting - matching AML report */
@page {
  size: A4 !important;
  margin: 0 !important;
}

/* Row and column spacing - matching AML report */
.row {
  margin-left: -5px !important;
  margin-right: -5px !important;
}

.col-md-6 {
  padding-left: 5px !important;
  padding-right: 5px !important;
}

.col-md-3 {
  padding-left: 5px !important;
  padding-right: 5px !important;
}

/* Responsive adjustments - matching AML report */
@media (max-width: 768px) {
  .sheet {
    padding: 20mm !important;
  }
  
  .director-header {
    flex-direction: column !important;
    align-items: flex-start !important;
  }
  
  .director-header .btn {
    margin-top: 15px !important;
  }
}

/* Professional Card Styles - inspired by reference components */
.overview-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.overview-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #007bff, #0056b3);
}

.overview-card .card-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem auto;
  color: white;
  font-size: 1.25rem;
}

.overview-card .card-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.overview-card .card-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #212529;
  margin-bottom: 0.25rem;
}

.overview-card .card-description {
  font-size: 0.75rem;
  color: #6c757d;
  line-height: 1.4;
}

/* Status Cards */
.status-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.25rem;
  transition: all 0.3s ease;
}

.status-card:hover {
  border-color: #007bff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.1);
}

.status-card .status-icon {
  font-size: 2rem;
  margin-bottom: 0.75rem;
}

.status-card .status-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.status-card .status-value {
  font-size: 1.125rem;
  font-weight: 600;
}

/* Info Items */
.info-item {
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

.info-item .info-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
  display: block;
}

.info-item .info-value {
  font-size: 0.875rem;
  color: #212529;
  font-weight: 500;
}

/* Summary Items */
.summary-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.summary-item .summary-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1rem;
  font-size: 1.125rem;
}

.summary-item .summary-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.summary-item .summary-value {
  font-size: 0.875rem;
  color: #212529;
  font-weight: 600;
}

/* ID Photo Styling */
.id-photo {
  max-width: 200px;
  max-height: 250px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 2px solid #e9ecef;
}

/* Header Styling */
.header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 8px;
  margin-bottom: 1.5rem;
  min-height: 140px;
  width: 100%;
  overflow: visible;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  box-sizing: border-box;
}

.header .logo {
  max-width: 250px;
  height: auto;
  object-fit: contain;
}

.header .page-info {
  text-align: right;
  min-width: 200px;
  flex-shrink: 0;
}

.header .page-indicator {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.header .h4 {
  font-size: 1.25rem;
  color: #495057;
  margin-bottom: 0.5rem;
}
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.metric-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: #007bff;
}

.metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #007bff, #0056b3);
}

.metric-card .card-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: white;
  font-size: 1.25rem;
}

.metric-card .metric-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.metric-card .metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #212529;
  margin-bottom: 0.25rem;
}

.metric-card .metric-description {
  font-size: 0.75rem;
  color: #6c757d;
  line-height: 1.4;
}

/* Details Section Styles */
.details-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
}

.details-section .section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.detail-item {
  background: white;
  border-radius: 6px;
  padding: 1rem;
  border-left: 3px solid #007bff;
}

.detail-item .detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.detail-item .detail-value {
  font-size: 0.875rem;
  color: #212529;
  word-break: break-word;
}

.detail-item .detail-value code {
  background: #f8f9fa;
  padding: 0.125rem 0.25rem;
  border-radius: 3px;
  font-size: 0.75rem;
  color: #e83e8c;
}

/* System Status Section Styles */
.system-status-section {
  margin-bottom: 2rem;
}

.subsection-title {
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.status-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.25rem;
  transition: all 0.3s ease;
}

.status-item:hover {
  border-color: #007bff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.1);
}

.status-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}

.status-header .status-name {
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
}

.status-value {
  margin-bottom: 0.5rem;
}

.status-description {
  font-size: 0.75rem;
  color: #6c757d;
  line-height: 1.4;
}

/* Verification Results Section Styles */
.verification-results-section {
  margin-bottom: 2rem;
}

.verification-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.verification-item {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.25rem;
  transition: all 0.3s ease;
}

.verification-item:hover {
  transform: translateY(-1px);
  border-color: #007bff;
  box-shadow: 0 6px 20px rgba(0, 123, 255, 0.1);
}

.verification-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.verification-header .verification-name {
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
}

.verification-result {
  margin-bottom: 0.75rem;
}

.verification-status {
  display: flex;
  align-items: center;
}

/* Technical Reference Styles */
.technical-reference {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.reference-card {
  background: linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%);
  border: 1px solid #bbdefb;
  border-radius: 6px;
  padding: 1rem;
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

.reference-card .reference-code {
  background: #fff3cd;
  color: #856404;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

/* Enhanced Badge Components */
.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Tracking Number Display */
.tracking-number-display {
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  margin: 1rem 0;
}

.tracking-number {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  color: #495057;
  background: #f8f9fa;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  display: inline-block;
  word-break: break-all;
  max-width: 100%;
  margin: 0.5rem 0;
}

/* Utility Classes */
.text-xs {
  font-size: 0.75rem;
}

.text-sm {
  font-size: 0.875rem;
}

.text-gradient {
  background: linear-gradient(135deg, #007bff, #0056b3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Responsive Design */
@media (max-width: 768px) {
  .metrics-grid,
  .status-grid,
  .verification-grid {
    grid-template-columns: 1fr;
  }
  
  .metric-card,
  .status-item,
  .verification-item {
    margin-bottom: 1rem;
  }
  
  .details-grid {
    grid-template-columns: 1fr;
  }
}

/* Content-aware responsive design */
@media print {
  /* Ensure content doesn't get cut off */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Header print optimization */
  .header {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    min-height: auto;
    height: auto;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  
  .header .logo {
    max-width: 200px;
    max-height: 80px;
  }
  
  .header .page-info {
    min-width: 150px;
  }
  
  /* Report meta print styles */
  .report-meta {
    break-inside: avoid;
    page-break-inside: avoid;
    margin: 1rem 0 0.5rem 0;
    padding: 0.75rem 1rem;
    font-size: 0.7rem;
  }
  
  .metrics-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 0.75rem;
  }
  
  .status-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 0.75rem;
  }
  
  .verification-grid {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 0.75rem;
  }
  
  /* Optimize for print */
  .card {
    margin-bottom: 1rem;
    box-shadow: none !important;
  }
  
  .metric-card,
  .status-item,
  .verification-item {
    break-inside: avoid;
    margin-bottom: 0.75rem;
  }
  
  /* Prevent page breaks in important sections */
  .director-header {
    break-after: avoid;
  }
  
  .section-title {
    break-after: avoid;
  }
  
  .subsection-title {
    break-after: avoid;
  }
}

/* Auto-sizing for different content volumes */
.auto-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

/* Report Meta Responsive */
@media screen and (max-width: 768px) {
  .report-meta {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
    padding: 1rem;
  }
}

/* Ensure proper spacing on all pages */
.page-content {
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
}

.page-content .report-meta {
  margin-top: auto;
}

.auto-grid-default {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.content-container {
  overflow: visible;
  height: auto;
}

.flexible-section {
  min-height: auto;
  height: fit-content;
  overflow: visible;
}

/* AML Report Specific Styles */
.card-header h6 {
  margin: 0 0 0 -16px;
  padding: 0 0 0 20px;
  border-left: 3px solid #59ff74;
  font-weight: 600;
  line-height: 1.5;
  font-size: 1rem;
}

/* Card styling for AML results */
.border-left-accent {
  border-left: 3px solid #59ff74 !important;
}

.nm-card.border-left-accent {
  border-left-color: #036016 !important;
}

.pep-card.border-left-accent {
  border-left-color: #358826 !important;
}

.wls-card.border-left-accent {
  border-left-color: #007313 !important;
}

.result-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Section specific styling */
.nm-result-card {
  border-radius: 6px;
  padding: 0.75rem;
  margin: -0.5rem;
}

.pep-result-card {
  border-radius: 6px;
  padding: 0.75rem;
  margin: -0.5rem;
}

.wls-result-card {
  border-radius: 6px;
  padding: 0.75rem;
  margin: -0.5rem;
}

/* Highlight emphasis styling */
em {
  background-color: #fff3cd;
  padding: 0.1rem 0.2rem;
  border-radius: 0.2rem;
  font-style: normal;
  font-weight: 600;
}

/* Provider box styling */
.provider-box {
  background: rgba(89, 255, 116, 0.1);
  border: 1px solid rgba(89, 255, 116, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
}

/* Info table styling */
.info-table td {
  padding: 0.5rem;
  border-bottom: 1px solid #eee;
}

.info-table td:first-child {
  font-weight: 600;
  color: #555;
  width: 30%;
}

/* Limit exceeded notice styles */
.limit-exceeded-notice {
  margin: 1.5rem 0;
}

.limit-card {
  border: 2px solid #036016;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(3, 96, 22, 0.15);
  overflow: hidden;
  position: relative;
}

.limit-card.nm-limit {
  border-color: #036016;
  box-shadow: 0 4px 12px rgba(3, 96, 22, 0.15);
}

.limit-card.pep-limit {
  border-color: #358826;
  box-shadow: 0 4px 12px rgba(16, 43, 22, 0.15);
}

.limit-card.watchlist-limit {
  border-color: #007313;
  box-shadow: 0 4px 12px rgba(0, 115, 19, 0.15);
}

.limit-header {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid rgba(3, 96, 22, 0.2);
}

.pep-limit .limit-header {
  border-bottom-color: rgba(16, 43, 22, 0.2);
}

.watchlist-limit .limit-header {
  border-bottom-color: rgba(0, 115, 19, 0.2);
}

.limit-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #036016;
  color: #fff;
  border-radius: 10px;
  margin-right: 0.75rem;
  font-size: 1.1rem;
}

.nm-limit .limit-icon {
  background: #036016;
}

.pep-limit .limit-icon {
  background: #358826;
}

.watchlist-limit .limit-icon {
  background: #007313;
}

.limit-title h6 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}

.limit-subtitle {
  font-size: 0.85rem;
  color: #666;
  font-weight: 500;
}

.limit-content {
  padding: 1rem 1.25rem 1.25rem;
}

.limit-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 8px;
  backdrop-filter: blur(5px);
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-label {
  font-size: 0.75rem;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #036016;
  line-height: 1;
  margin-top: 0.25rem;
}

.pep-limit .stat-value {
  color: #358826;
}

.watchlist-limit .stat-value {
  color: #007313;
}

.stat-divider {
  font-size: 0.9rem;
  color: #999;
  font-weight: 500;
  margin: 0 0.5rem;
}

.limit-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: #555;
}

.limit-message i {
  color: #036016;
  font-size: 0.95rem;
}

.pep-limit .limit-message i {
  color: #358826;
}

.watchlist-limit .limit-message i {
  color: #007313;
}

.limit-action {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  font-size: 0.85rem;
  color: #444;
  font-weight: 500;
}
</style>

